<template>
  <v-dialog v-model="show" v-bind="win.dialogProps" persistent>
    <v-card rounded="lg" class="mail-compose-card nm-window" :ref="win.setCardEl">
      <!-- Header (zugleich Drag-Griff; Doppelklick maximiert/stellt wieder her) -->
      <div
        class="mail-compose-header dialog-window-handle"
        @pointerdown="win.onHandlePointerDown"
        @dblclick="win.onHandleDblClick"
      >
        <span class="mail-compose-title">
          {{ $gt('Neue Nachricht') }}
          <span v-if="draftSavedAt" class="mail-draft-indicator" :title="draftSavedAtAbs">
            {{ $gt('· Entwurf gespeichert') }} {{ draftSavedRel }}
          </span>
          <span v-if="isUrgent" class="nm-head-urgent">
            <TriangleAlert :size="12" />
            {{ $gt('Dringend') }}
          </span>
        </span>
        <div class="nm-head-actions">
          <button
            type="button"
            class="nm-head-btn"
            :title="$gt('Minimieren')"
            :aria-label="$gt('Minimieren')"
            @click="win.minimize"
          >
            <Minus :size="16" />
          </button>
          <button
            type="button"
            class="nm-head-btn"
            :title="win.isMaximized ? $gt('Wiederherstellen') : $gt('Maximieren')"
            :aria-label="win.isMaximized ? $gt('Wiederherstellen') : $gt('Maximieren')"
            @click="win.toggleMaximize"
          >
            <Minimize2 v-if="win.isMaximized" :size="14" />
            <Maximize2 v-else :size="14" />
          </button>
          <button
            type="button"
            class="nm-head-btn"
            :title="$gt('Schließen')"
            :aria-label="$gt('Schließen')"
            data-testid="compose-close"
            @click="closeKeepingDraft"
          >
            <X :size="16" />
          </button>
        </div>
      </div>

      <div class="mail-compose-fields">
        <!-- Fall -->
        <div class="mail-field-row" ref="caseWrap">
          <label class="mail-field-label">{{ $gt('Fall') }}</label>
          <div class="nm-case-wrap">
            <div v-if="selectedCase" class="nm-case-chip" @click="clearCase">
              <FolderOpen :size="12" />
              <span class="nm-case-chip-num">{{ selectedCase.caseNumber }}</span>
              <span class="nm-case-chip-desc">{{ truncate(selectedCase.caseDescription, 38) }}</span>
              <X :size="13" class="nm-case-chip-x" />
            </div>
            <div v-else class="nm-case-input-wrap">
              <Search :size="14" class="nm-case-icon" />
              <input
                ref="caseInput"
                v-model="caseQuery"
                class="nm-case-input"
                :placeholder="$gt('Fall suchen (Nummer, Beschreibung)…')"
                @focus="showCaseDropdown = true"
              />
            </div>
            <transition name="nm-dd">
              <div v-if="showCaseDropdown && !selectedCase" class="nm-case-dropdown">
                <div v-if="casesLoading" class="nm-case-empty">
                  <v-progress-circular indeterminate size="20" width="2" color="primary" />
                </div>
                <template v-else>
                  <div
                    v-for="c in filteredCases"
                    :key="c.id"
                    class="nm-case-opt"
                    @click="selectCase(c, { openPicker: true })"
                  >
                    <FolderOpen :size="13" />
                    <div class="nm-case-opt-text">
                      <span class="nm-case-opt-num">{{ c.caseNumber }}</span>
                      <span class="nm-case-opt-desc">{{ c.caseDescription || '' }}</span>
                    </div>
                  </div>
                  <div v-if="filteredCases.length === 0" class="nm-case-empty">
                    {{ $gt('Kein Fall gefunden') }}
                  </div>
                </template>
              </div>
            </transition>
          </div>
          <!-- Optionen rechts neben der Fallauswahl (Inhaber-Feedback I.3) -->
          <div class="nm-topopts">
            <button
              v-if="selectedCase"
              type="button"
              class="nm-topopt-btn"
              :title="$gt('Beteiligte des Falls als Empfänger auswählen')"
              @click="openRecipientPicker"
            >
              <Users :size="13" />
              <span>{{ $gt('Empfänger aus Akte') }}</span>
            </button>
            <label
              class="nm-topopt-check"
              :class="{ 'nm-topopt-check--on': isUrgent }"
              :title="$gt('Die Nachricht wird beim Empfänger in KARS als dringend hervorgehoben und priorisiert angezeigt (Eskalation).')"
            >
              <input type="checkbox" v-model="isUrgent" class="nm-topopt-input" />
              <TriangleAlert :size="12" />
              <span>{{ $gt('Als dringend markieren (Eskalation)') }}</span>
              <Info :size="12" class="nm-topopt-info" />
            </label>
            <label
              class="nm-topopt-check"
              :class="{ 'nm-topopt-check--on': requestDeliveryReceipt }"
              :title="$gt('Sie erhalten eine Bestätigung, sobald die Nachricht zugestellt wurde.')"
            >
              <input type="checkbox" v-model="requestDeliveryReceipt" class="nm-topopt-input" />
              <span>{{ $gt('Übertragungsbestätigung anfordern') }}</span>
              <Info :size="12" class="nm-topopt-info" />
            </label>
            <label
              class="nm-topopt-check"
              :class="{ 'nm-topopt-check--on': requestReadReceipt }"
              :title="$gt('Sie erhalten eine Bestätigung, sobald der Empfänger die Nachricht gelesen hat.')"
            >
              <input type="checkbox" v-model="requestReadReceipt" class="nm-topopt-input" />
              <span>{{ $gt('Lesebestätigung anfordern') }}</span>
              <Info :size="12" class="nm-topopt-info" />
            </label>
          </div>
        </div>

        <!-- Empfängerzeilen: An / Cc / Bcc (Outlook-artig, immer eingeblendet) -->
        <template v-for="row in recipientRows" :key="row.key">
          <div class="mail-field-row">
            <label class="mail-field-label" :title="row.hint ? $gt(row.hint) : undefined">
              {{ $gt(row.label) }}
              <Info v-if="row.hint" :size="11" class="nm-label-info" />
            </label>
            <div
              class="mail-recipients-wrap"
              :class="{ 'mail-recipients-wrap--active': activeRow === row.key }"
              @click="focusRowInput(row.key)"
            >
              <div
                v-for="chip in chipsFor(row.key)"
                :key="chip.key"
                class="mail-recipient-tag nm-chip"
                :title="chip.title"
              >
                <span class="mail-recipient-initials" :style="{ background: chip.color }">
                  <AtSign v-if="chip.external" :size="10" />
                  <template v-else>{{ chip.initials }}</template>
                </span>
                <span class="nm-chip-label">{{ chip.label }}</span>
                <button
                  type="button"
                  class="nm-chip-menu-btn"
                  :title="$gt('In andere Zeile verschieben')"
                  @click.stop="toggleChipMenu(chip)"
                >
                  <ChevronDown :size="12" />
                </button>
                <button type="button" class="mail-recipient-remove" @click.stop="removeChip(chip)"><X :size="12" /></button>
                <transition name="nm-dd">
                  <div v-if="openChipMenu === chip.key + ':' + row.key" class="nm-chip-menu" @mousedown.stop>
                    <button
                      v-for="target in rowsExcept(row.key)"
                      :key="target.key"
                      type="button"
                      class="nm-chip-menu-item"
                      @click.stop="moveChip(chip, target.key)"
                    >
                      {{ $gt(target.moveLabel) }}
                    </button>
                  </div>
                </transition>
              </div>
              <div class="mail-recipient-add-wrap">
                <input
                  :ref="(el) => setRowInputRef(row.key, el)"
                  v-model="rowQueries[row.key]"
                  type="text"
                  class="mail-recipient-inline-input"
                  :placeholder="rowPlaceholder(row.key)"
                  @focus="activeRow = row.key"
                  @blur="onRecipientBlur(row.key)"
                  @keydown.enter.prevent="onRecipientEnter(row.key)"
                  @keydown.tab="onRecipientTab($event, row.key)"
                  @keydown="onRecipientKeydown($event, row.key)"
                />
              </div>
            </div>
          </div>
        </template>

        <!-- Kanzlei Polz 18.10.2026: abweichende Korrespondenzperson des
             Kunden auch beim Schreiben sichtbar – mit wem statt dem Kunden
             korrespondiert wird; Übernahme als Empfänger per Klick. -->
        <div v-if="correspondenceClients.length" class="mail-field-row nm-corr-row" data-testid="compose-correspondence">
          <div class="nm-corr-list">
            <CorrespondenceNotice
              v-for="entry in correspondenceClients"
              :key="entry.key"
              :client="entry.client"
              :client-label="entry.label"
              :show-reason="canSeeCorrespondenceReason"
            >
              <template v-if="entry.recipient" #actions>
                <button
                  v-if="!isExternalSelected(entry.recipient.email)"
                  type="button"
                  class="nm-corr-add"
                  data-testid="compose-correspondence-add"
                  @click="addExternalEmail(entry.recipient.email, entry.recipient.name, 'to')"
                >
                  <UserPlus :size="13" aria-hidden="true" />{{ $gt('Als Empfänger hinzufügen') }}
                </button>
                <span v-else class="nm-corr-added"><Check :size="13" aria-hidden="true" />{{ $gt('Ist Empfänger') }}</span>
              </template>
            </CorrespondenceNotice>
          </div>
        </div>

        <!-- Betreff mit Bausteinen (Kundenfeedback 24.10.2026, XI): Die
             Bausteine aus den Falldaten stehen jetzt IN der Betreffzeile –
             gewählte (= im Betreff enthaltene) sind blau und lassen sich per
             Klick wieder abwählen. Die Suche heißt, was sie tut. -->
        <div class="mail-field-row nm-subject-row">
          <label class="mail-field-label" for="nm-subject-input">{{ $gt('Betreff') }}</label>
          <div class="nm-subject">
            <input
              id="nm-subject-input"
              v-model="composeSubject"
              class="mail-field-input"
              data-testid="compose-subject"
              :placeholder="$gt('Betreff eingeben…')"
            />
            <div v-if="selectedCase" class="nm-subject-parts" data-testid="subject-parts">
              <span
                class="nm-subject-parts__caption"
                :title="$gt('Angaben aus der Akte per Klick in den Betreff übernehmen. Blau = steht bereits im Betreff; erneuter Klick nimmt sie wieder heraus.')"
              >
                {{ $gt('In den Betreff:') }}
                <Info :size="11" />
              </span>
              <template v-if="cockpitQueryActive">
                <button
                  v-for="hit in cockpitHits"
                  :key="hit.key"
                  type="button"
                  class="nm-suggest-chip"
                  :class="{ 'nm-suggest-chip--on': subjectHasPart(composeSubject, hit.value) }"
                  :aria-pressed="subjectHasPart(composeSubject, hit.value) ? 'true' : 'false'"
                  :title="subjectHasPart(composeSubject, hit.value) ? $gt('Aus dem Betreff entfernen') : $gt('In den Betreff übernehmen')"
                  @click="toggleSubject(hit.value)"
                >
                  <span class="nm-suggest-chip-label">{{ hit.label }}</span>
                  <span class="nm-suggest-chip-value">{{ hit.value }}</span>
                </button>
                <span v-if="!cockpitHits.length" class="nm-cockpit-empty">{{ $gt('Keine Treffer in den Falldaten') }}</span>
              </template>
              <template v-else>
                <button
                  v-for="s in subjectSuggestions"
                  :key="s.key"
                  type="button"
                  class="nm-suggest-chip"
                  :class="{ 'nm-suggest-chip--on': subjectHasPart(composeSubject, s.value) }"
                  :aria-pressed="subjectHasPart(composeSubject, s.value) ? 'true' : 'false'"
                  :data-testid="`subject-part-${s.key}`"
                  :title="subjectHasPart(composeSubject, s.value) ? $gt('Aus dem Betreff entfernen') : $gt('In den Betreff übernehmen')"
                  @click="toggleSubject(s.value)"
                >
                  <span class="nm-suggest-chip-label">{{ $gt(s.label) }}</span>
                  <span class="nm-suggest-chip-value">{{ s.value }}</span>
                </button>
              </template>
              <div
                class="nm-cockpit-search"
                :class="{ 'nm-cockpit-search--active': cockpitQueryActive }"
                :title="$gt('Durchsucht alle Angaben der Akte (z. B. Kennzeichen, Schadennummer, Gutachten-Nr.). Treffer erscheinen links und lassen sich per Klick in den Betreff übernehmen.')"
              >
                <Search :size="12" class="nm-cockpit-search__icon" />
                <input
                  v-model="cockpitQuery"
                  class="nm-cockpit-search__input"
                  data-testid="subject-search"
                  :placeholder="$gt('Weitere Angaben für den Betreff suchen …')"
                  :aria-label="$gt('Weitere Angaben aus der Akte für den Betreff suchen')"
                  @keydown.escape.stop="cockpitQuery = ''"
                  @keydown.enter.prevent="appendFirstCockpitHit"
                />
                <button
                  v-if="cockpitQuery"
                  type="button"
                  class="nm-cockpit-search__clear"
                  :aria-label="$gt('Suche leeren')"
                  @click="cockpitQuery = ''"
                >
                  <X :size="12" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Rückmeldung zur eingefügten Vorlage; die Knöpfe „Vorlage einfügen“
           und „Als Vorlage speichern“ stehen in der gemeinsamen
           Werkzeugleiste unten (Kundenfeedback 25.10.2026 Teil 3, I). -->
      <div v-if="insertedTemplate" class="nm-template-bar">
        <span class="nm-template-hint" :class="{ 'nm-template-hint--warn': insertedTemplate.missing > 0 }">
          <TriangleAlert v-if="insertedTemplate.missing > 0" :size="12" />
          <Check v-else :size="12" />
          {{ $gt('Vorlage') }} „{{ insertedTemplate.name }}“ {{ $gt('eingefügt') }}<template v-if="insertedTemplate.missing > 0"> · {{ insertedTemplate.missing }} {{ $gt('Platzhalter bitte ergänzen (gelb markiert)') }}</template>
        </span>
      </div>

      <!-- Rich-Text-Composer (geteilt mit dem Unterhaltungs-Fenster) -->
      <MessageComposer
        ref="composer"
        v-model="composeContent"
        class="nm-composer"
        :placeholder="$gt('Nachricht verfassen…')"
      />

      <!-- Attachments -->
      <div v-if="composeAttachments.length" class="mail-pending-attachments mail-pending-attachments--compose">
        <span class="nm-attach-label">{{ $gt('Anhänge') }}:</span>
        <div v-for="(f, i) in composeAttachments" :key="i" class="mail-pending-file">
          <Paperclip :size="13" />
          <span>{{ f.name || f.filename }}</span>
          <span
            v-if="f.docId && f.caseNumber && (!selectedCase || Number(f.idCase) !== Number(selectedCase.id))"
            class="mail-pending-case"
            :title="$gt('Dokument aus einer anderen Akte')"
          >{{ f.caseNumber }}</span>
          <span class="mail-pending-size">{{ formatFileSize(f.size || f.fileSize) }}</span>
          <button class="mail-pending-remove" @click="composeAttachments.splice(i, 1)"><X :size="13" /></button>
        </div>
      </div>

      <!-- Werkzeugleiste: dieselbe Komponente wie beim Antworten in der
           Korrespondenz und im Unterhaltungs-Fenster (Kundenfeedback
           25.10.2026 Teil 3, I). Rechts die Versand-Knöpfe dieses Fensters. -->
      <div class="mail-compose-bottom mail-compose-bottom--dialog">
        <ComposeTools
          ref="composeTools"
          :before-leave="leaveForTemplateSettings"
          :cases="cases"
          :composer="composerHandle"
          :html="composeContent"
          :case-type="selectedCase ? selectedCase.caseType : ''"
          :picker-case="selectedCase"
          :signature="sig"
          :subject="composeSubject"
          :suggest-category="suggestTemplateCategory"
          :template-values="templateValues()"
          class="nm-tools"
          @attach-case="onPickerAttach"
          @attach-files="addFiles"
          @changed="scheduleDraftSave"
          @update:signature-on="showSignature = $event"
          @template-inserted="onTemplateInserted"
        >
          <template #end>
            <!-- Ablage in der Akte nach dem Senden (Kundenfeedback 24.10.2026, IV.2) -->
            <SendFilingControl
              v-if="selectedCase"
              :ctl="sendFiling"
              :attachment-count="composeAttachments.length"
            />
            <button class="mail-action-btn mail-action-btn--discard" data-testid="compose-discard" @click="discard" :title="$gt('Verwerfen')">
              <Trash2 :size="16" />
              <span class="mail-action-btn__label">{{ $gt('Verwerfen') }}</span>
            </button>
            <ButtonBase buttonText="Senden" iconType="send" variant="flat" :loading="sending" :disabled="sending" @click="send" />
          </template>
        </ComposeTools>
      </div>

      <!-- Resize-Handles an Kanten und Ecken (nicht im maximierten Zustand) -->
      <template v-if="!win.isMaximized">
        <div
          v-for="dir in resizeDirs"
          :key="'rh-' + dir"
          class="nm-resize"
          :class="'nm-resize--' + dir"
          @pointerdown="win.onResizePointerDown($event, dir)"
        ></div>
      </template>
    </v-card>

    <!-- Empfängerauswahl aus der Akte (RA-Micro-artig, Inhaber-Feedback I.3) -->
    <v-dialog v-model="showRecipientPicker" max-width="820" persistent>
      <v-card rounded="lg">
        <v-card-title class="d-flex justify-space-between align-center">
          <span>{{ $gt('Empfänger aus Akte wählen') }}</span>
          <ButtonBase iconStyle iconType="cancel" color="grey" @click="closeRecipientPicker" />
        </v-card-title>
        <v-card-text class="pa-4 pt-0">
          <table class="nm-picker-table">
            <thead>
              <tr>
                <th class="nm-picker-th nm-picker-th--name">{{ $gt('E-Mailadresse') }}</th>
                <th class="nm-picker-th">{{ $gt('An') }}</th>
                <th class="nm-picker-th">{{ $gt('Cc') }}</th>
                <th class="nm-picker-th">{{ $gt('Bcc') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in pickerRows" :key="row.key" class="nm-picker-tr">
                <!-- Name zuerst, dann die deutsche Rolle (Kundenfeedback 24.10.2026, IV.12). -->
                <td class="nm-picker-td nm-picker-td--name" :data-testid="`recipient-row-${row.email}`">
                  <span class="nm-picker-name">{{ row.name || row.email }}</span>
                  <span v-if="row.subline" class="nm-picker-role"> – {{ $gt(row.subline) }}</span>
                  <span class="nm-picker-email">{{ row.email }}</span>
                </td>
                <td v-for="role in ['to', 'cc', 'bcc']" :key="role" class="nm-picker-td nm-picker-td--check">
                  <button
                    type="button"
                    class="nm-picker-check"
                    :class="{ 'nm-picker-check--on': row.sel === role }"
                    :aria-label="$gt(role === 'to' ? 'An' : role === 'cc' ? 'Cc' : 'Bcc')"
                    @click="togglePickerRole(row, role)"
                  >
                    <Check v-if="row.sel === role" :size="13" />
                  </button>
                </td>
              </tr>
              <tr v-if="pickerRows.length === 0">
                <td colspan="4" class="nm-picker-empty">{{ $gt('Keine Beteiligten mit E-Mail-Adresse in dieser Akte.') }}</td>
              </tr>
            </tbody>
          </table>
        </v-card-text>
        <v-divider />
        <v-card-actions class="d-flex align-center ma-2">
          <button type="button" class="mail-action-btn" @click="openAddParticipant" :title="$gt('Neuen Beteiligten anlegen/ändern, z. B. wenn die E-Mail-Adresse fehlt')">
            <UserPlus :size="15" />
            <span class="mail-action-btn__label">{{ $gt('Neuen Beteiligten anlegen/ändern') }}</span>
          </button>
          <v-spacer />
          <ButtonBase buttonText="Schließen" iconType="cancel" @click="closeRecipientPicker" />
          <ButtonBase buttonText="Übernehmen" iconType="check" variant="flat" @click="applyRecipientPicker" />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Beteiligten direkt aus dem Compose-Fenster heraus anlegen -->
    <AddParticipantDialog
      v-if="selectedCase"
      ref="addParticipantDialog"
      :case-id="Number(selectedCase.id)"
      :case-title="selectedCase.caseDescription || selectedCase.caseNumber || ''"
      :existing-participants="involvedForDialog"
      @added="onParticipantAdded"
      @remove="onParticipantRemoveRequest"
      @add-mandant="onAddMandantRequest"
    />

    <!-- Kundenreview 23.08.26, XVII: Beim Verwerfen klare Optionen statt
         Browser-confirm. Kundenfeedback 26.10.2026, V: Wer „Verwerfen“
         klickt, bekommt „Verwerfen“ als Hauptknopf (auch per Enter) – vorher
         war das „Speichern“, und der Entwurf tauchte später wieder auf. -->
    <YesNoDialog
      ref="refDiscardDialog"
      :title="$gt('E-Mail verwerfen?')"
      :content="$gt('Die angefangene E-Mail wird gelöscht und nicht unter „Entwürfe“ gespeichert.')"
      type="warning"
      yesButtonText="Verwerfen"
      yesButtonIconType="delete"
      :onYes="discardDeleteDraft"
      :noButtonText="discardKeepLabel"
      noButtonIconType="save"
      :onNo="discardSaveDraft"
      hasCancelButton
      :cancelButtonText="discardContinueLabel"
      cancelButtonIconType="cancel"
      :onCancel="() => {}"
    />

    <!-- Rückfrage, wenn der Betreff schon gefüllt ist -->
    <YesNoDialog
      ref="refSubjectDialog"
      :title="$gt('Betreff ersetzen?')"
      :content="subjectDialogText"
      type="warning"
      yesButtonText="Ersetzen"
      yesButtonIconType="check"
      :onYes="replaceSubjectFromTemplate"
      noButtonText="Behalten"
      noButtonIconType="cancel"
    />
  </v-dialog>
</template>

<script>
import { ref } from "vue";
import { cleanRecipientName } from "@/components/messages/recipientName";
import { API_BASE_URL } from "@/config";
import { useSnackbarStore } from "@/stores/snackbarStore.ts";
import { collectCockpitEntries, isCockpitQueryActive, searchCockpitEntries } from "./subjectCockpitSearch";
import { addSubjectPart, subjectHasPart, toggleSubjectPart } from "./subjectParts";
import { useAuthStore } from "@/stores/authStore.ts";
import { getRequestGeneric } from "@/services/backendApiGeneric";
import {
  composeDraftIdFromKey, deleteDraft, newComposeDraftId, readComposeDraft, requestedComposeDraftKey, saveComposeDraft,
} from "@/services/messageDrafts";
import { composeFingerprint, isPristineCompose } from "./composeDraftFingerprint";
import { useDialogWindow } from "@/composables/useDialogWindow";
import ButtonBase from "@/components/buttons/ButtonBase.vue";
import YesNoDialog from "@/components/windows/dialogs/YesNoDialog.vue";
import AddParticipantDialog from "@/pages/pageCases/pageCaseDetail/sections/AddParticipantDialog.vue";
import CorrespondenceNotice from "@/components/correspondence/CorrespondenceNotice.vue";
import { canManageCorrespondence, clientDisplayName, correspondenceRecipient, hasCorrespondencePerson } from "@/utils/correspondencePerson";
import MessageComposer from "@/components/messages/MessageComposer.vue";
import { gtKey } from "@/i18n/gt";
import ComposeTools from "@/components/messages/compose/ComposeTools.vue";
import SendFilingControl from "@/components/messages/compose/SendFilingControl.vue";
import { useSendFiling } from "@/components/messages/compose/useSendFiling";
import { fileSentMessage } from "@/components/messages/compose/sendFiling";
import { useComposeSignature } from "@/components/messages/compose/useComposeSignature";
import { initialBody, insertQuotedHistory } from "@/components/messages/compose/composeBody";
import { composeTemplateValues, suggestTemplateCategory } from "@/components/messages/compose/composeTemplate";
import { loadCaseComposeResources } from "@/components/messages/compose/composeCaseData";
import { useInsuranceDirectory } from "@/composables/useInsuranceDirectory";
import { buildRecipientCandidates } from "@/utils/mailRecipientCandidates";
import {
  FolderOpen, Search, X, UserPlus, Paperclip, Check, Trash2,
  Users, AtSign, Info,
  Minus, Maximize2, Minimize2, ChevronDown, TriangleAlert, MailPlus,
} from "lucide-vue-next";

const AVATAR_COLORS = ["#6b7280"];

/** Empfängerzeilen im Kopfbereich (Outlook-artig: An / Cc / Bcc). */
const RECIPIENT_ROWS = [
  { key: "to", label: gtKey("An"), moveLabel: gtKey('Nach "An" verschieben'), hint: "" },
  {
    key: "cc",
    label: gtKey("Cc"),
    moveLabel: gtKey('Nach "Cc" verschieben'),
    hint: gtKey("Cc (Kopie): Diese Empfänger erhalten die Nachricht in Kopie. Alle Empfänger können die Cc-Adressen sehen."),
  },
  {
    key: "bcc",
    label: gtKey("Bcc"),
    moveLabel: gtKey('Nach "Bcc" verschieben'),
    hint: gtKey("Bcc (Blindkopie): Diese Empfänger erhalten die Nachricht in Kopie, ohne dass andere Empfänger sie sehen können."),
  },
];

/** Richtungen der Fenster-Resize-Handles (Kanten + Ecken). */
const RESIZE_DIRS = ["n", "s", "e", "w", "ne", "nw", "se", "sw"];

/** Knopftexte des „Verwerfen“-Dialogs (ButtonBase übersetzt sie selbst). */
const DISCARD_KEEP_LABEL = gtKey("Als Entwurf behalten");
const DISCARD_CONTINUE_LABEL = gtKey("Weiter bearbeiten");

export default {
  name: "NewMessageDialog",

  components: {
    ButtonBase, AddParticipantDialog, YesNoDialog, MessageComposer, CorrespondenceNotice,
    ComposeTools, SendFilingControl,
    FolderOpen, Search, X, UserPlus, Paperclip, Check, Trash2,
    Users, AtSign, Info,
    Minus, Maximize2, Minimize2, ChevronDown, TriangleAlert,
  },

  emits: ["sent", "closed"],

  setup() {
    // `show` lebt als Setup-Ref, damit useDialogWindow den Offen-Zustand
    // beobachten kann; über `this.show` weiterhin wie ein Data-Feld nutzbar.
    const show = ref(false);
    const win = useDialogWindow({
      id: "new-message",
      title: gtKey("Neue Nachricht"),
      icon: MailPlus,
      isOpen: show,
      resizable: {
        minWidth: 560,
        minHeight: 420,
        defaultWidth: "min(1240px, 72vw)",
        defaultHeight: "min(900px, 76vh)",
      },
      // Kundenfeedback 16.10.2026 (Kanzlei Polz): „man kann die Mail
      // schreiben, aber währenddessen sonst nirgendwo rein." Nicht-modal:
      // kein Scrim, keine Scroll-Sperre, keine Fokus-Falle – Cockpit & Co.
      // bleiben bedienbar; das Fenster lebt app-global (App.vue).
      modeless: true,
    });
    // Ablage beim Versenden (Kundenfeedback 24.10.2026, IV.2). Fall und
    // Anhangszahl spiegeln Watcher aus den Optionen hierher.
    const filingCaseId = ref(null);
    const filingAttachmentCount = ref(0);
    const sendFiling = useSendFiling(() => filingCaseId.value, () => filingAttachmentCount.value);
    // Signatur (Standortwahl, Vorauswahl, Darstellung) – dasselbe Composable
    // wie beim Antworten (Kundenfeedback 25.10.2026 Teil 3, I). Die Felder
    // stehen unter ihren bisherigen Namen auch direkt an `this`.
    const sig = useComposeSignature();
    return { show, win, sendFiling, filingCaseId, filingAttachmentCount, sig, ...sig };
  },

  data() {
    return {
      cases: [],
      casesLoading: false,
      selectedCase: null,
      caseQuery: "",
      showCaseDropdown: false,

      caseUsers: [],
      caseInvolved: [],
      // Empfänger mit Rolle: Nutzer als { userId, role }, externe Adressen
      // als { email, name, role } mit role = "to" | "cc" | "bcc".
      selectedRecipients: [],
      selectedExternals: [],
      participantsLoading: false,
      recipientDropdown: false,
      rowQueries: { to: "", cc: "", bcc: "" },
      rowInputEls: {},
      activeRow: "to",
      openChipMenu: null,

      composeSubject: "",
      composeContent: "",
      composeAttachments: [],
      isUrgent: false,
      requestDeliveryReceipt: false,
      requestReadReceipt: false,
      sending: false,

      // Empfängerauswahl aus der Akte (RA-Micro-artiges Auswahlfenster)
      showRecipientPicker: false,
      pickerRows: [],

      // Falldaten für Betreff-Vorschläge (best-effort geladen)
      caseAccidents: [],
      caseClients: [],
      caseOpponents: [],
      caseVehicles: [],
      caseSettlement: null,
      caseWitnesses: [],
      casePoliceProsecution: [],
      // „Cockpit durchsuchen“ (Inhaber-Feedback 15.08.26, I.6)
      cockpitQuery: "",

      // Fahrzeug-Kontext (per-Fahrzeug-Trennung bei Mehrfahrzeug-Fällen)
      contextVehicleId: null,

      // Auto-Draft (Gmail-Style, lokal in localStorage – Issue #206)
      draftSavedAt: null,
      draftTimer: null,
      draftTickTimer: null,
      draftNow: Date.now(),
      restoringDraft: false,
      // Kundenfeedback 16.09.26: Fingerabdruck der automatischen Vorbelegung
      // (Betreff, Signatur, Empfänger). Solange er unverändert ist, entsteht
      // kein Entwurf. null = nichts gemerkt (z. B. wiederhergestellter Entwurf).
      draftBaseline: null,
      draftRestored: false,
      // Kundenfeedback 26.10.2026, V: Jedes Fenster hat einen eigenen Entwurf
      // (draftId); draftKey ist der Speicherplatz, unter dem er zuletzt lag.
      draftId: newComposeDraftId(),
      draftKey: null,

      // Signatur steht im Text (Knopf „Signatur“ in der Werkzeugleiste).
      // Standort und Vorlage der Signatur liefert useComposeSignature (setup).
      showSignature: true,

      // Dokumente aus Fallakten (Anhänge per Referenz).
      docListCache: {},

      // Rückfrage-Dialog, wenn eine Vorlage einen Betreff mitbringt und der
      // Betreff schon gefüllt ist.
      pendingTemplateSubject: "",
      insertedTemplate: null,

      _handleDocClick: null,
      _involvedIdsBefore: null,
    };
  },

  computed: {
    /** Kunden des Falls mit abweichender Korrespondenzperson (Hinweis unter den Empfängern). */
    correspondenceClients() {
      const clients = Array.isArray(this.caseClients) ? this.caseClients : [];
      const withPerson = clients.filter(hasCorrespondencePerson);
      return withPerson.map((client, index) => ({
        key: client.id ?? `idx-${index}`,
        client,
        label: withPerson.length > 1 || clients.length > 1 ? clientDisplayName(client) : "",
        recipient: correspondenceRecipient(client),
      }));
    },
    canSeeCorrespondenceReason() {
      return canManageCorrespondence(useAuthStore().organizationRoles);
    },
    filteredCases() {
      const q = this.caseQuery.trim().toLowerCase();
      if (!q) return this.cases.slice(0, 50);
      return this.cases.filter((c) => {
        return (c.caseNumber || "").toLowerCase().includes(q) ||
               (c.caseDescription || "").toLowerCase().includes(q);
      }).slice(0, 50);
    },
    resizeDirs() {
      return RESIZE_DIRS;
    },
    discardKeepLabel() {
      return DISCARD_KEEP_LABEL;
    },
    discardContinueLabel() {
      return DISCARD_CONTINUE_LABEL;
    },
    // Alle Zeilen (An/Cc/Bcc) sind immer eingeblendet (Inhaber-Feedback I.7/I.10).
    recipientRows() {
      return RECIPIENT_ROWS;
    },
    availableRecipients() {
      const currentUserId = useAuthStore().idUser;
      return this.caseUsers.filter(
        (u) => u.idUser !== currentUserId && !this.selectedRecipients.some((r) => r.userId === u.idUser),
      );
    },
    hasAnyRecipient() {
      return this.selectedRecipients.length > 0 || this.selectedExternals.length > 0;
    },
    involvedForDialog() {
      return (this.caseInvolved || []).filter((p) => !p.removedAt);
    },
    /**
     * Betreff-Vorschläge aus den Falldaten (Inhaber-Feedback I.6): anklickbare
     * Bausteine wie Unfalldatum, Kunde, Kennzeichen. Nur befüllte Werte.
     */
    subjectSuggestions() {
      if (!this.selectedCase) return [];
      const out = [];
      const push = (key, label, value) => {
        const v = (value || "").toString().trim();
        if (v && !out.some((s) => s.value === v)) out.push({ key, label, value: v });
      };

      const acc = (this.caseAccidents || [])[0];
      if (acc?.accidentDate) push("unfalldatum", "Unfalldatum", this.formatDateDE(acc.accidentDate));

      for (const cl of this.caseClients || []) {
        const name = cl.client || [cl.lastName, cl.firstName].filter(Boolean).join(", ") || cl.company;
        push("kunde-" + cl.id, "Kunde", name);
      }
      for (const v of this.caseVehicles || []) {
        push("kfz-" + v.id, "Kunde Kennzeichen", v.licensePlate);
      }
      for (const o of this.caseOpponents || []) {
        push("gegner-kfz-" + o.id, "Gegner Kennzeichen", o.licensePlate);
        const holder = o.holderCompany || [o.holderLastName, o.holderFirstName].filter(Boolean).join(", ");
        push("gegner-" + o.id, "Gegner", holder);
      }
      if (this.selectedCase.caseNumber) push("az", "Aktenzeichen", this.selectedCase.caseNumber);

      // Versicherungsschein-/Schadennummern und Gutachten-Nr. (Inhaber-
      // Feedback 15.08.26 I.6, Nachtrag): nur, wo im Fall erfasst.
      for (const o of this.caseOpponents || []) {
        push("gegn-vs-" + o.id, "Gegn. Versicherung VS-Nr.", o.insurancePolicyNumber);
        push("gegn-schaden-" + o.id, "Gegn. Versicherung Schaden-Nr.", o.insuranceCaseNumber);
        push("reg-vs-" + o.id, "Regulierungsbeauftr. VS-Nr.", o.adjusterPolicyNumber);
        push("reg-schaden-" + o.id, "Regulierungsbeauftr. Schaden-Nr.", o.adjusterCaseNumber);
      }
      for (const v of this.caseVehicles || []) {
        push("kasko-vs-" + v.id, "Kasko VS-Nr.", v.insurancePolicyNumber);
        push("kasko-schaden-" + v.id, "Kasko Schaden-Nr.", v.insuranceCaseNumber);
      }
      for (const cl of this.caseClients || []) {
        push("rs-vs-" + cl.id, "Rechtsschutz VS-Nr.", cl.legalExpenseInsurancePolicyNumber);
        push("rs-schaden-" + cl.id, "Rechtsschutz Schaden-Nr.", cl.legalExpenseInsuranceCaseNumber);
      }
      push("gutachten-nr", "Gutachten-Nr.", this.caseSettlement?.expertReportNumberAccordingToClient);
      return out;
    },
    subjectDialogText() {
      return this.pendingTemplateSubject
        ? `Der Betreff ist bereits gefüllt. Soll er durch „${this.pendingTemplateSubject}“ ersetzt werden?`
        : "";
    },

    /**
     * „Cockpit durchsuchen“ (Inhaber-Feedback 15.08.26, I.6): alle Textwerte
     * der Falldaten als durchsuchbare Einträge (Label = Feldbezeichnung aus
     * backend/resources/fields_*.js).
     */
    cockpitEntries() {
      if (!this.selectedCase) return [];
      return collectCockpitEntries([
        {
          id: "case",
          records: [{ caseNumber: this.selectedCase.caseNumber, caseDescription: this.selectedCase.caseDescription }],
        },
        { id: "clients", records: this.caseClients || [] },
        { id: "vehicles", records: this.caseVehicles || [] },
        { id: "opponents", records: this.caseOpponents || [] },
        { id: "accident", records: this.caseAccidents || [] },
        { id: "witnesses", records: this.caseWitnesses || [] },
        { id: "policeProsecution", records: this.casePoliceProsecution || [] },
        { id: "settlement", records: [this.caseSettlement] },
      ]);
    },
    cockpitQueryActive() {
      return isCockpitQueryActive(this.cockpitQuery);
    },
    cockpitHits() {
      return searchCockpitEntries(this.cockpitEntries, this.cockpitQuery);
    },
    draftSavedRel() {
      return this.draftSavedAt ? this.formatDraftRel(this.draftSavedAt) : "";
    },
    draftSavedAtAbs() {
      return this.draftSavedAt ? new Date(this.draftSavedAt).toLocaleString("de-DE") : "";
    },
  },

  watch: {
    selectedCase(c) { this.filingCaseId = c ? Number(c.id) : null; },
    "composeAttachments.length"(n) { this.filingAttachmentCount = n; },
    composeContent() { this.scheduleDraftSave(); },
    composeSubject() { this.scheduleDraftSave(); },
    selectedRecipients: { handler() { this.scheduleDraftSave(); }, deep: true },
    selectedExternals: { handler() { this.scheduleDraftSave(); }, deep: true },
    showSignature() { this.scheduleDraftSave(); },
    isUrgent() { this.scheduleDraftSave(); },
    requestDeliveryReceipt() { this.scheduleDraftSave(); },
    requestReadReceipt() { this.scheduleDraftSave(); },
  },

  methods: {
    async open(opts = {}) {
      // Fenster ist bereits offen (seit 16.10.2026 nicht-modal und app-global,
      // also erreichbar): Eine angefangene Nachricht darf nicht still
      // überschrieben werden. Mit Inhalt → nur nach vorn holen; ohne Inhalt →
      // Laufzeit aufräumen und wie gewohnt neu vorbelegen.
      if (this.show) {
        if (!this.isComposePristine()) {
          this.win.bringToFront();
          useSnackbarStore().showInfo(this.$gt("„Neue Nachricht“ ist bereits geöffnet – bitte zuerst senden oder verwerfen."));
          return;
        }
        this.teardownRuntime();
      }
      this.reset();
      this.sendFiling.reset();
      this.show = true;
      this.contextVehicleId = opts?.idVehicle ?? null;
      await this.loadUserLocations();
      await this.loadOrgSignature();
      this.applyInitialEditorContent();
      await this.$nextTick();
      this.attachOutsideClick();
      // Live-Aktualisierung des "vor X Sek."-Indikators + Draft-Sicherung beim Verlassen
      this.draftTickTimer = setInterval(() => { this.draftNow = Date.now(); }, 15000);
      window.addEventListener("beforeunload", this.flushDraft);
      await this.loadCases();
      // Optionale Vorbelegung von Fall & Empfänger (z. B. aus dem Cockpit
      // oder aus der Fallakte – dort ist der Fall immer vorausgewählt).
      if (opts && opts.caseId) {
        const found = this.cases.find((c) => Number(c.id) === Number(opts.caseId))
          // Fallback: Fall-Kontext direkt übernehmen, falls die Fallliste ihn
          // (noch) nicht enthält – z. B. beim Öffnen aus der Fallakte.
          || {
            id: Number(opts.caseId),
            caseNumber: opts.caseNumber || "",
            caseDescription: opts.caseDescription || "",
            caseType: opts.caseType || "",
          };
        await this.selectCase(found, { openPicker: false });
        if (opts.recipientId) {
          const ridNum = Number(opts.recipientId);
          const u = this.caseUsers.find((x) => Number(x.idUser) === ridNum);
          if (u && !this.selectedRecipients.some((r) => r.userId === u.idUser)) {
            this.selectedRecipients.push({ userId: u.idUser, role: "to" });
          }
        }
        if (opts.prefillExternal && opts.prefillExternal.email) {
          this.addExternalEmail(opts.prefillExternal.email, opts.prefillExternal.name || "", "to");
        }
        this.applyComposePrefill(opts);
        await this.applyInitialAttachments(opts);
        this.openRequestedDraft(opts);
        await this.markComposePristine();
        // Kasko-Schreiben aus dem Kasko-Cockpit (Mail RA Polz 13.10.2026):
        // gleich mit dem Vorlagen-Fenster öffnen, Kasko-Schreiben stehen oben.
        if (opts.openTemplates) {
          this.$nextTick(() => this.$refs.composeTools?.openTemplatePicker());
          return;
        }
        this.$nextTick(() => {
          // Fokus in den Betreff oder den Editor verschieben. Die Wurzel
          // dieser Komponente ist der teleportierte v-dialog (kein Element),
          // daher über das Fenster im Dokument suchen.
          const subj = document.querySelector(".nm-window .mail-field-input");
          if (subj && typeof subj.focus === "function") subj.focus();
        });
        return;
      }
      // Ohne Fall-Kontext: Nachricht ist trotzdem möglich (Inhaber-Feedback
      // I.1 – kein Zwang zur Fallauswahl). Interne Kolleg:innen als
      // Empfänger-Vorschläge laden.
      await this.loadOrgUsers();
      // Externer Empfänger (z. B. Kunde aus dem Kontakte-Reiter) auch ohne Fall.
      if (opts && opts.prefillExternal && opts.prefillExternal.email) {
        this.addExternalEmail(opts.prefillExternal.email, opts.prefillExternal.name || "", "to");
      }
      this.applyComposePrefill(opts);
      await this.applyInitialAttachments(opts);
      this.openRequestedDraft(opts);
      await this.markComposePristine();
      this.$nextTick(() => this.focusRowInput("to"));
    },

    /**
     * Vorbelegte Anhänge (Inhaber-Feedback 15.08.26, I.12):
     * `attachments: [{ idCase, idDocument }]` – Dokumente aus Fallakten;
     * `attachmentFiles: File[]` – bereits erzeugte Dateien (z. B. das PDF
     * einer weitergeleiteten Nachricht).
     */
    async applyInitialAttachments(opts = {}) {
      if (Array.isArray(opts.attachmentFiles)) {
        for (const f of opts.attachmentFiles) {
          if (f instanceof File) this.composeAttachments.push(f);
        }
      }
      if (Array.isArray(opts.attachments) && opts.attachments.length) {
        await this.addAttachmentRefs(opts.attachments);
      }
    },

    /**
     * Vorbelegung von Betreff/Inhalt, z. B. beim Weiterleiten einer
     * Nachricht (Kundenreview 23.08.26, XX). Der Inhalt wird UNTER der
     * Signatur angehängt, damit die eigene Signatur erhalten bleibt.
     */
    applyComposePrefill(opts = {}) {
      if (!opts.prefillSubject && !opts.prefillHtml) return;
      if (opts.prefillSubject) this.composeSubject = opts.prefillSubject;
      if (!opts.prefillHtml) return;

      // Reihenfolge wie in Outlook (Kundenfeedback 14.09.26): eigener Text,
      // darunter die Signatur, darunter der zitierte E-Mail-Verkehr. Der
      // zitierte Teil wird deshalb gezielt HINTER dem Signaturblock
      // eingefügt — ein blindes Anhängen ans Editor-Ende setzt die Signatur
      // unter den kompletten Verlauf, sobald sie erst nach der Vorbelegung
      // in den Editor kommt (Signatur-Nachtrag, Standortwechsel).
      // Gemeinsame Regel mit dem Antwortfeld (compose/composeBody).
      const ed = this.editorEl();
      const html = insertQuotedHistory(ed ? ed.innerHTML : this.composeContent, opts.prefillHtml);
      if (ed) ed.innerHTML = html;
      this.composeContent = html;
    },

    /** Schließen (auch beim Abmelden): Entwurf bleibt erhalten (Gmail-Verhalten). */
    close() {
      this.flushDraft();
      this.closeWindow();
    },

    /**
     * Schließen über „X“ bzw. „Als Entwurf behalten“. Da eine neue E-Mail
     * leer startet (Kundenfeedback 26.10.2026, V), sagen wir, wo der
     * Entwurf zu finden ist.
     */
    closeKeepingDraft() {
      this.close();
      if (this.draftKey) useSnackbarStore().showInfo(this.$gt("Entwurf gespeichert – Sie finden ihn unter „Entwürfe“."));
    },

    /** „Verwerfen“: Dialog mit klaren Optionen (Kundenreview 23.08.26, XVII). */
    discard() {
      const hasContent = this.stripHtml(this.composeContent).trim()
        || this.composeSubject.trim()
        || this.selectedRecipients.length
        || this.selectedExternals.length;
      if (!hasContent || this.isComposePristine()) {
        this.clearDraft();
        this.closeWindow();
        return;
      }
      this.$refs.refDiscardDialog?.openDialog();
    },

    /** Dialog-Option „Verwerfen“: Entwurf endgültig löschen, Fenster schließen. */
    discardDeleteDraft() {
      this.clearDraft();
      this.closeWindow();
    },

    /** Dialog-Option „Als Entwurf behalten“: Entwurf sichern, Fenster schließen. */
    discardSaveDraft() {
      this.closeKeepingDraft();
    },

    /** Timer und Dokument-Listener eines offenen Fensters freigeben. */
    teardownRuntime() {
      this.detachOutsideClick();
      if (this.draftTickTimer) { clearInterval(this.draftTickTimer); this.draftTickTimer = null; }
      if (this.draftTimer) { clearTimeout(this.draftTimer); this.draftTimer = null; }
      window.removeEventListener("beforeunload", this.flushDraft);
    },

    /** Interner Teardown ohne Draft-Seiteneffekte. */
    closeWindow() {
      this.show = false;
      this.teardownRuntime();
      this.$emit("closed");
    },

    reset() {
      this.selectedCase = null;
      this.caseQuery = "";
      this.showCaseDropdown = false;
      this.caseUsers = [];
      this.caseInvolved = [];
      this.selectedRecipients = [];
      this.selectedExternals = [];
      this.recipientDropdown = false;
      this.rowQueries = { to: "", cc: "", bcc: "" };
      this.activeRow = "to";
      this.openChipMenu = null;
      this.isUrgent = false;
      this.requestDeliveryReceipt = false;
      this.requestReadReceipt = false;
      this.showRecipientPicker = false;
      this.pickerRows = [];
      this.caseAccidents = [];
      this.caseClients = [];
      this.caseOpponents = [];
      this.caseVehicles = [];
      this.caseSettlement = null;
      this.caseWitnesses = [];
      this.casePoliceProsecution = [];
      this.cockpitQuery = "";
      this._involvedIdsBefore = null;
      this.composeSubject = "";
      this.composeContent = "";
      this.composeAttachments = [];
      this.docListCache = {};
      // Review PR #1170: Ordner können sich zwischen zwei Nachrichten ändern
      // (neu angelegt, umbenannt) – den Baum mit den Dokumenten verwerfen.
      this.folderTargetCache = {};
      this.pickerFolders = [];
      this.pickerClosedFolders = {};
      this.sending = false;
      this.showSignature = true;
      this.contextVehicleId = null;
      this.draftSavedAt = null;
      this.restoringDraft = false;
      this.pendingTemplateSubject = "";
      this.insertedTemplate = null;
      this.draftBaseline = null;
      this.draftRestored = false;
      this.draftId = newComposeDraftId();
      this.draftKey = null;
    },

    applyInitialEditorContent() {
      const sigHtml = initialBody(this.getRenderedSignature());
      this.composeContent = sigHtml;
      this.$nextTick(() => {
        const ed = this.editorEl();
        if (ed) ed.innerHTML = sigHtml;
      });
    },

    /** contenteditable-Element des geteilten Composers (MessageComposer). */
    editorEl() {
      return this.$refs.composer?.editorEl() || null;
    },

    async loadCases() {
      this.casesLoading = true;
      try {
        const data = await getRequestGeneric("/protected/cases");
        const list = Array.isArray(data) ? data : (data?.cases || []);
        this.cases = list.map((c) => ({
          id: c.id ?? c.idCase,
          caseNumber: c.caseNumber || c.number || "",
          caseDescription: c.caseDescription || c.description || c.title || "",
          caseType: c.caseType || "",
        })).filter((c) => c.id);
      } catch (e) {
        useSnackbarStore().showSnackbar({ message: "Fälle konnten nicht geladen werden", type: "error" });
      } finally {
        this.casesLoading = false;
      }
    },

    async selectCase(c, { openPicker = false } = {}) {
      // Fallwechsel in einem noch unberührten Fenster: die neue Vorbelegung
      // (Betreff zur Akte) ist wieder nur Vorbelegung.
      const wasPristine = this.isComposePristine();
      this.selectedCase = c;
      this.showCaseDropdown = false;
      this.caseQuery = "";
      this.selectedRecipients = [];
      this.selectedExternals = [];
      this.rowQueries = { to: "", cc: "", bcc: "" };
      this.openChipMenu = null;
      this.caseUsers = [];
      this.caseInvolved = [];
      await Promise.all([
        this.loadCaseUsers(),
        this.loadCaseInvolved(),
        this.loadSubjectData(),
      ]);
      // Betreff sinnvoll vorbelegen, wenn noch leer. Ein vorhandener Entwurf
      // zu diesem Fall wird NICHT automatisch geladen (Kundenfeedback
      // 26.10.2026, V) – Entwürfe öffnet man bewusst unter „Entwürfe“.
      if (!this.composeSubject.trim()) {
        const num = (c.caseNumber || "").trim();
        const desc = (c.caseDescription || "").trim();
        if (num && desc) this.composeSubject = `[${num}] ${desc}`;
        else if (num) this.composeSubject = `Akte ${num}`;
        else if (desc) this.composeSubject = desc;
      }
      if (wasPristine) await this.markComposePristine();
      // Direkt nach der Fallauswahl das Empfängerauswahl-Fenster öffnen
      // (Inhaber-Feedback I.3, RA-Micro-Verhalten).
      if (openPicker) this.openRecipientPicker();
    },

    clearCase() {
      // Der Entwurf bleibt beim Fenster und wandert beim nächsten Sichern zum
      // neuen Fall – keine Kopie beim alten Fall, die später wieder auftaucht.
      this.selectedCase = null;
      this.caseUsers = [];
      this.caseInvolved = [];
      this.selectedRecipients = [];
      this.selectedExternals = [];
      this.caseAccidents = [];
      this.caseClients = [];
      this.caseOpponents = [];
      this.caseVehicles = [];
      this.caseSettlement = null;
      this.caseWitnesses = [];
      this.casePoliceProsecution = [];
      this.cockpitQuery = "";
      this.showCaseDropdown = true;
      this.loadOrgUsers();
      this.$nextTick(() => this.$refs.caseInput?.focus());
    },

    /**
     * Falldaten für die Betreff-Vorschläge laden (best-effort, Fehler werden
     * ignoriert – dann fehlen lediglich einzelne Vorschläge).
     */
    async loadSubjectData() {
      if (!this.selectedCase) return;
      const id = this.selectedCase.id;
      const data = await loadCaseComposeResources(id);
      // Fall inzwischen gewechselt: veraltete Antwort verwerfen – sonst stünde
      // z. B. die Korrespondenzperson eines anderen Falls als Empfänger-Vorschlag da.
      if (!this.selectedCase || this.selectedCase.id !== id) return;
      this.caseAccidents = data.accidents;
      this.caseClients = data.clients;
      this.caseOpponents = data.opponents;
      this.caseVehicles = data.vehicles;
      this.caseSettlement = data.settlement;
      this.caseWitnesses = data.witnesses;
      this.casePoliceProsecution = data.policeProsecution;
    },

    /**
     * „Cockpit durchsuchen“ (Inhaber-Feedback 15.08.26, I.6): Enter übernimmt
     * den ersten Treffer in den Betreff.
     */
    appendFirstCockpitHit() {
      const hit = this.cockpitHits[0];
      if (!hit) return;
      this.appendToSubject(hit.value);
      this.cockpitQuery = "";
    },

    /** Datum YYYY-MM-DD → DD.MM.YYYY (für Betreff-Vorschläge). */
    formatDateDE(iso) {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
      return m ? `${m[3]}.${m[2]}.${m[1]}` : (iso || "");
    },

    /** Betreff-Baustein anhängen (mit Trennzeichen, ohne Dopplung). */
    appendToSubject(value) {
      this.composeSubject = addSubjectPart(this.composeSubject, value);
    },

    /** Steht der Baustein im Betreff? (Chip blau, Kundenfeedback 24.10.2026, XI) */
    subjectHasPart(subject, value) {
      return subjectHasPart(subject, value);
    },

    /** Baustein an- bzw. wieder abwählen (XI). */
    toggleSubject(value) {
      this.composeSubject = toggleSubjectPart(this.composeSubject, value);
    },

    /**
     * Interne Kolleg:innen als Empfänger-Vorschläge laden, wenn kein Fall
     * gewählt ist (fallfreie Nachricht, Inhaber-Feedback I.1).
     */
    async loadOrgUsers() {
      try {
        const store = useAuthStore();
        if (!store.idLocation) return;
        const res = await fetch(
          `${API_BASE_URL}/protected/locations/${store.idLocation}/available-users`,
          { credentials: "include" },
        );
        if (!res.ok) return;
        const data = await res.json();
        const currentUserId = store.idUser;
        this.caseUsers = (Array.isArray(data) ? data : [])
          .filter((u) => u.id !== currentUserId)
          .map((u) => ({
            idUser: u.id,
            userName: u.userName || [u.firstName, u.lastName].filter(Boolean).join(" "),
            initials: u.initials || "?",
          }));
      } catch { /* silent */ }
    },

    async loadCaseUsers() {
      if (!this.selectedCase) return;
      this.participantsLoading = true;
      try {
        const res = await fetch(`${API_BASE_URL}/protected/cases/${this.selectedCase.id}/conversations/users`, { credentials: "include" });
        if (res.ok) this.caseUsers = await res.json();
      } catch { /* silent */ }
      this.participantsLoading = false;
    },

    async loadCaseInvolved() {
      if (!this.selectedCase) return;
      try {
        const res = await fetch(`${API_BASE_URL}/protected/cases/${this.selectedCase.id}/involved`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          this.caseInvolved = Array.isArray(data) ? data : (data && data.involved) || [];
        }
      } catch { this.caseInvolved = []; }
    },

    // ── Auto-Draft (Gmail-Style, localStorage – gleiche Keys wie
    //    services/messageDrafts.ts und der "Entwürfe"-Tab) ─────────────────

    /**
     * Bewusst geöffneter Entwurf (Ordner „Entwürfe“ → `draftKey`). Ohne
     * diese Angabe startet „Neue Nachricht“ immer leer – nur mit Signatur
     * (Kundenfeedback 26.10.2026, V: Ein verworfener Entwurf mit Vorlage
     * tauchte bei jeder neuen E-Mail zum selben Fall wieder auf).
     */
    openRequestedDraft(opts = {}) {
      const key = requestedComposeDraftKey(opts);
      if (!key) return false;
      const restored = this.restoreDraft(key);
      if (!restored) useSnackbarStore().showInfo(this.$gt("Der Entwurf ist nicht mehr vorhanden."));
      return restored;
    },

    /**
     * Stellt den Entwurf unter `key` wieder her. Das Fenster übernimmt
     * dessen Speicherplatz, damit Senden oder Verwerfen genau ihn entfernt.
     * Rückgabe true, wenn ein Entwurf geladen wurde.
     */
    restoreDraft(key) {
      const draft = readComposeDraft(key);
      if (!draft) return false;

      this.restoringDraft = true;
      this.draftRestored = true;
      this.draftBaseline = null;
      this.draftKey = key;
      // Alt-Format (ein Entwurf je Fall) bekommt beim nächsten Sichern eine
      // eigene Kennung; der alte Eintrag wird dabei ersetzt.
      this.draftId = composeDraftIdFromKey(key) || newComposeDraftId();
      this.composeSubject = draft.subject;
      this.composeContent = draft.content;
      // Abwärtskompatibel: alte Entwürfe speichern Empfänger als nackte
      // User-IDs, neue als { userId, role }.
      this.selectedRecipients = draft.recipients
        .map((r) => (typeof r === "object" && r !== null)
          ? { userId: r.userId, role: r.role || "to" }
          : { userId: r, role: "to" })
        .filter((r) => r.userId != null);
      this.selectedExternals = draft.externals.map((e) => ({ ...e }));
      this.showSignature = draft.showSignature;
      // Gewählten Signatur-Standort wiederherstellen (nur wenn der Nutzer
      // dem Standort weiterhin zugeordnet ist); Vorlage passend nachladen.
      if (draft.signatureLocationId != null
        && this.userLocations.some((l) => l.id === draft.signatureLocationId)
        && draft.signatureLocationId !== this.signatureLocationId) {
        this.signatureLocationId = draft.signatureLocationId;
        this.resolveSignatureForLocation();
      }
      this.isUrgent = draft.isUrgent;
      this.requestDeliveryReceipt = draft.requestDeliveryReceipt;
      this.requestReadReceipt = draft.requestReadReceipt;
      this.draftSavedAt = draft.savedAt || Date.now();
      this.$nextTick(() => {
        const ed = this.editorEl();
        if (ed) ed.innerHTML = this.composeContent;
        this.restoringDraft = false;
      });
      return true;
    },

    /** Draft-Key: Fall-Id oder 0 für fallfreie Nachrichten. */
    draftCaseId() {
      return this.selectedCase ? this.selectedCase.id : 0;
    },

    /** Inhaltlicher Stand des Dialogs für den Entwurfs-Fingerabdruck. */
    composeState() {
      return {
        subject: this.composeSubject,
        html: this.composeContent,
        recipients: this.selectedRecipients,
        externals: this.selectedExternals,
        attachmentCount: this.composeAttachments.length,
      };
    },

    /**
     * Stand nach der automatischen Vorbelegung merken — nicht nach einem
     * wiederhergestellten Entwurf, der ist bereits ein echter Entwurf.
     */
    async markComposePristine() {
      await this.$nextTick();
      if (this.draftRestored) return;
      if (this.draftTimer) { clearTimeout(this.draftTimer); this.draftTimer = null; }
      this.draftBaseline = composeFingerprint(this.composeState());
    },

    isComposePristine() {
      return isPristineCompose(this.composeState(), this.draftBaseline);
    },

    scheduleDraftSave() {
      if (this.restoringDraft || !this.show) return;
      if (this.draftTimer) clearTimeout(this.draftTimer);
      this.draftTimer = setTimeout(() => this.flushDraft(), 1200);
    },

    flushDraft() {
      if (this.draftTimer) { clearTimeout(this.draftTimer); this.draftTimer = null; }
      if (!this.show) return;
      const stripped = (this.composeContent || "").replace(/<[^>]*>/g, "").trim();
      if (!stripped && !this.composeSubject.trim() && !this.selectedRecipients.length && !this.selectedExternals.length) {
        this.clearDraft();
        return;
      }
      // Unberührte Vorbelegung (Betreff zur Akte + Signatur) ist kein Entwurf.
      if (this.isComposePristine()) {
        this.clearDraft();
        return;
      }
      const now = Date.now();
      const key = saveComposeDraft({
        previousKey: this.draftKey,
        caseId: this.draftCaseId(),
        draftId: this.draftId,
        data: {
          subject: this.composeSubject,
          content: this.composeContent,
          recipients: this.selectedRecipients.map((r) => ({ ...r })),
          externals: this.selectedExternals.map((e) => ({ ...e })),
          showSignature: this.showSignature,
          signatureLocationId: this.signatureLocationId ?? null,
          isUrgent: this.isUrgent,
          requestDeliveryReceipt: this.requestDeliveryReceipt,
          requestReadReceipt: this.requestReadReceipt,
          savedAt: now,
        },
      });
      if (key) {
        this.draftKey = key;
        this.draftSavedAt = now;
      }
    },

    /** Entwurf dieses Fensters entfernen (Senden, Verwerfen, leerer Inhalt). */
    clearDraft() {
      if (this.draftTimer) { clearTimeout(this.draftTimer); this.draftTimer = null; }
      if (this.draftKey) deleteDraft(this.draftKey);
      this.draftKey = null;
      this.draftSavedAt = null;
    },

    formatDraftRel(ts) {
      if (!ts) return "";
      // referenziert this.draftNow für Reaktivität
      const diffSec = Math.max(0, Math.floor((this.draftNow - ts) / 1000));
      if (diffSec < 5) return "gerade eben";
      if (diffSec < 60) return `vor ${diffSec} Sek.`;
      const diffMin = Math.floor(diffSec / 60);
      if (diffMin < 60) return `vor ${diffMin} Min.`;
      const d = new Date(ts);
      return "um " + d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    },

    addRecipient(userId, role) {
      const targetRow = role || this.activeRow;
      this.ensureRowVisible(targetRow);
      if (!this.selectedRecipients.some((r) => r.userId === userId)) {
        this.selectedRecipients.push({ userId, role: targetRow });
      }
      this.rowQueries[targetRow] = "";
      this.recipientDropdown = false;
      this.$nextTick(() => this.focusRowInput(targetRow));
    },

    removeChip(chip) {
      this.openChipMenu = null;
      if (chip.external) {
        this.selectedExternals = this.selectedExternals.filter((x) => x.email !== chip.email);
      } else {
        this.selectedRecipients = this.selectedRecipients.filter((r) => r.userId !== chip.userId);
      }
    },

    /** Chips (Nutzer + Externe) einer Empfängerzeile in Anzeige-Reihenfolge. */
    chipsFor(role) {
      const users = this.selectedRecipients
        .filter((r) => r.role === role)
        .map((r) => {
          const u = this.getUserById(r.userId);
          return {
            key: "u-" + r.userId,
            external: false,
            userId: r.userId,
            role,
            initials: u?.initials || "?",
            label: u?.userName || "?",
            title: u?.userName || "",
            color: this.avatarColor(r.userId),
          };
        });
      const externals = this.selectedExternals
        .filter((x) => x.role === role)
        .map((x) => ({
          key: "e-" + x.email,
          external: true,
          email: x.email,
          role,
          label: x.name ? `${x.name} <${x.email}>` : x.email,
          title: x.email,
          color: "#7a7a7a",
        }));
      return [...users, ...externals];
    },

    rowsExcept(key) {
      return RECIPIENT_ROWS.filter((r) => r.key !== key);
    },

    toggleChipMenu(chip) {
      const id = chip.key + ":" + chip.role;
      this.openChipMenu = this.openChipMenu === id ? null : id;
    },

    /** Verschiebt eine Empfänger-Pille in eine andere Zeile (An/Cc/Bcc). */
    moveChip(chip, targetRow) {
      this.openChipMenu = null;
      this.ensureRowVisible(targetRow);
      if (chip.external) {
        this.selectedExternals = this.selectedExternals.map((x) =>
          x.email === chip.email ? { ...x, role: targetRow } : x,
        );
      } else {
        this.selectedRecipients = this.selectedRecipients.map((r) =>
          r.userId === chip.userId ? { ...r, role: targetRow } : r,
        );
      }
    },

    /** Alle Zeilen sind dauerhaft sichtbar – kein Einblenden mehr nötig. */
    ensureRowVisible() { /* noop – An/Cc/Bcc sind immer eingeblendet */ },

    showRecipientRow(row) {
      this.activeRow = row;
      this.$nextTick(() => this.focusRowInput(row));
    },

    setActiveRow(row) {
      this.activeRow = row;
    },

    setRowInputRef(row, el) {
      this.rowInputEls[row] = el;
    },

    focusRowInput(row) {
      const key = row || this.activeRow;
      this.activeRow = key;
      this.rowInputEls[key]?.focus?.();
    },

    rowPlaceholder(row) {
      if (this.chipsFor(row).length > 0) return "";
      return "Name oder E-Mail-Adresse…";
    },

    getUserById(id) {
      return this.caseUsers.find((u) => u.idUser === id);
    },

    isValidEmail(value) {
      const v = (value || "").trim();
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    },

    isExternalSelected(email) {
      const e = (email || "").trim().toLowerCase();
      return this.selectedExternals.some((x) => x.email === e);
    },

    addExternalEmail(email, name, role) {
      const e = (email || "").trim().toLowerCase();
      if (!this.isValidEmail(e)) return false;
      const targetRow = role || this.activeRow;
      this.ensureRowVisible(targetRow);
      if (!this.selectedExternals.some((x) => x.email === e)) {
        // Kundenfeedback 18.09.2026: Namen aus der Beteiligtenliste kommen als
        // „Firma <adresse>“ – nur der Name wird Anzeigename, sonst entsteht
        // beim Versand eine doppelte, ungültige Adresse.
        this.selectedExternals.push({ email: e, name: cleanRecipientName(name, e), role: targetRow });
      }
      return true;
    },

    commitExternalFromQuery(row, { focusAfter = true } = {}) {
      const key = row || this.activeRow;
      const raw = (this.rowQueries[key] || "").trim();
      if (!raw) return false;
      let email = raw;
      let name = "";
      const m = raw.match(/^\s*(.*?)\s*<([^>]+)>\s*$/);
      if (m) {
        name = (m[1] || "").trim().replace(/^"|"$/g, "");
        email = (m[2] || "").trim();
      }
      email = email.toLowerCase();
      if (!this.isValidEmail(email)) return false;
      this.addExternalEmail(email, name, key);
      this.rowQueries[key] = "";
      if (focusAfter) this.$nextTick(() => this.focusRowInput(key));
      return true;
    },

    // ── Empfängerauswahl aus der Akte (Auswahlfenster, Inhaber-Feedback I.3) ──

    /**
     * Baut die Zeilen des Auswahlfensters: Beteiligte, Sachbearbeiter und
     * Abteilungen der Akte sowie alle Versicherungen – jeweils nur mit
     * E-Mail-Adresse (Kundenfeedback 24.10.2026, IV.12 und X). Die
     * Mitarbeitenden des eigenen Unternehmens werden nicht mehr gelistet.
     * Bereits gewählte Empfänger sind vorbelegt.
     */
    async openRecipientPicker() {
      if (!this.selectedCase) return;
      // Bereits offen (z. B. nach „Neuen Beteiligten anlegen“): Auswahl behalten.
      this.pickerRows = this.recipientPickerRows(this.showRecipientPicker ? this.pickerRows : []);
      this.showRecipientPicker = true;
      // Versicherungsadressen kommen ggf. aus dem Verzeichnis (wie auf der
      // Beteiligten-Karte) – nach dem Laden die Zeilen ergänzen.
      const directory = useInsuranceDirectory();
      if (!directory.loaded.value) {
        await directory.ensureLoaded();
        if (this.showRecipientPicker) this.pickerRows = this.recipientPickerRows(this.pickerRows);
      }
    },

    /** Zeilen aus Beteiligten und Falldaten; Auswahl aus `previous` bleibt erhalten. */
    recipientPickerRows(previous = []) {
      const directory = useInsuranceDirectory();
      const candidates = buildRecipientCandidates(this.involvedForDialog, {
        opponents: this.caseOpponents,
        clients: this.caseClients,
        vehicles: this.caseVehicles,
        isKaskoCase: String(this.selectedCase?.caseType || "").toLowerCase() === "kasko",
        lookup: (name) => directory.matchInsurance(name),
      });
      return candidates.map((c) => {
        const before = previous.find((r) => r.email === c.email);
        const existing = this.selectedExternals.find((x) => x.email === c.email);
        return {
          key: c.key,
          kind: "external",
          email: c.email,
          name: c.name,
          subline: c.role,
          sel: before ? before.sel : (existing ? existing.role : null),
        };
      });
    },

    /** Schließen ohne Übernehmen: Auswahl wird verworfen (I.3). */
    closeRecipientPicker() {
      this.showRecipientPicker = false;
      this.pickerRows = [];
    },

    /** An/Cc/Bcc je Zeile exklusiv umschalten (erneuter Klick hebt auf). */
    togglePickerRole(row, role) {
      row.sel = row.sel === role ? null : role;
    },

    /** Auswahl in die Empfängerzeilen übernehmen. */
    applyRecipientPicker() {
      for (const row of this.pickerRows) {
        if (row.kind === "external") {
          const existing = this.selectedExternals.find((x) => x.email === row.email);
          if (row.sel && !existing) {
            this.selectedExternals.push({ email: row.email, name: row.name, role: row.sel });
          } else if (row.sel && existing) {
            this.selectedExternals = this.selectedExternals.map((x) =>
              x.email === row.email ? { ...x, role: row.sel } : x,
            );
          } else if (!row.sel && existing) {
            this.selectedExternals = this.selectedExternals.filter((x) => x.email !== row.email);
          }
        } else {
          const existing = this.selectedRecipients.find((r) => r.userId === row.userId);
          if (row.sel && !existing) {
            this.selectedRecipients.push({ userId: row.userId, role: row.sel });
          } else if (row.sel && existing) {
            this.selectedRecipients = this.selectedRecipients.map((r) =>
              r.userId === row.userId ? { ...r, role: row.sel } : r,
            );
          } else if (!row.sel && existing) {
            this.selectedRecipients = this.selectedRecipients.filter((r) => r.userId !== row.userId);
          }
        }
      }
      this.closeRecipientPicker();
    },

    /** Getippte gültige E-Mail beim Verlassen des Feldes übernehmen (I.10). */
    onRecipientBlur(row) {
      if (this.isValidEmail((this.rowQueries[row] || "").trim())) {
        this.commitExternalFromQuery(row, { focusAfter: false });
      }
    },

    // ── Werkzeugleiste (ComposeTools, gemeinsam mit dem Antworten) ───────

    /** Der geteilte Rich-Text-Composer für die Werkzeugleiste. */
    composerHandle() {
      return this.$refs.composer || null;
    },

    /** Falldaten für Vorlagen-Platzhalter und die Empfänger-Vorauswahl. */
    composeCaseData() {
      if (!this.selectedCase) return null;
      return {
        caseNumber: this.selectedCase.caseNumber,
        caseType: this.selectedCase.caseType,
        clients: this.caseClients,
        vehicles: this.caseVehicles,
        opponents: this.caseOpponents,
        accidents: this.caseAccidents,
        involved: this.involvedForDialog,
      };
    },

    /**
     * Empfänger-Vorauswahl im Vorlagen-Fenster (Kundenfeedback 25.10.2026
     * Teil 2, II): der erste externe Empfänger in „An“ (sonst Cc/Bcc).
     */
    suggestTemplateCategory() {
      if (!this.selectedCase || this.selectedExternals.length === 0) return "";
      const first = this.selectedExternals.find((x) => x.role === "to") || this.selectedExternals[0];
      try {
        const directory = useInsuranceDirectory();
        return suggestTemplateCategory(first.email, this.composeCaseData(), (name) => directory.matchInsurance(name));
      } catch {
        // Vorauswahl ist nur eine Hilfe – ohne sie zeigt das Fenster „Alle“.
        return "";
      }
    },

    /** Platzhalter-Werte: Nutzer, Signatur-Standort und Akte (wie beim Antworten). */
    templateValues() {
      return composeTemplateValues(this.sig.senderInfo(), this.composeCaseData());
    },

    /**
     * Vorlage eingefügt (Text setzt die Werkzeugleiste): Betreff übernehmen,
     * bei bereits gefülltem Betreff mit Rückfrage.
     */
    onTemplateInserted(tpl) {
      if (tpl.subject) {
        const current = this.composeSubject.trim();
        if (!current || current === tpl.subject) {
          this.composeSubject = tpl.subject;
        } else {
          this.pendingTemplateSubject = tpl.subject;
          this.$nextTick(() => this.$refs.refSubjectDialog?.openDialog());
        }
      }
      this.insertedTemplate = { name: tpl.name, missing: tpl.missing };
      this.scheduleDraftSave();
    },

    replaceSubjectFromTemplate() {
      if (this.pendingTemplateSubject) this.composeSubject = this.pendingTemplateSubject;
      this.pendingTemplateSubject = "";
    },

    /**
     * Vor dem Wechsel in die Vorlagenverwaltung (Kundenfeedback 25.10.2026
     * Teil 2, III): Entwurf sichern und das Fenster minimieren – Browser-
     * Zurück führt wieder hierher.
     */
    leaveForTemplateSettings() {
      this.flushDraft();
      this.win.minimize();
    },

    userSuggestionsFor(row) {
      const q = (this.rowQueries[row] || "").trim().toLowerCase();
      const list = this.availableRecipients;
      if (!q) return list.slice(0, 8);
      return list
        .filter((u) => (u.userName || "").toLowerCase().includes(q))
        .slice(0, 8);
    },

    onRecipientEnter(row) {
      const q = (this.rowQueries[row] || "").trim();
      const first = this.userSuggestionsFor(row)[0];
      if (first && q) {
        if (this.isValidEmail(q)) {
          this.commitExternalFromQuery(row);
          return;
        }
        this.addRecipient(first.idUser, row);
        return;
      }
      this.commitExternalFromQuery(row);
    },

    onRecipientTab(e, row) {
      const q = (this.rowQueries[row] || "").trim();
      if (!q) return;
      if (this.isValidEmail(q)) {
        e.preventDefault();
        this.commitExternalFromQuery(row);
      } else {
        const suggestions = this.userSuggestionsFor(row);
        if (suggestions.length === 1) {
          e.preventDefault();
          this.addRecipient(suggestions[0].idUser, row);
        }
      }
    },

    onRecipientKeydown(e, row) {
      if (e.key === "," || e.key === ";") {
        e.preventDefault();
        if (!this.commitExternalFromQuery(row)) {
          const first = this.userSuggestionsFor(row)[0];
          if (first) this.addRecipient(first.idUser, row);
        }
        return;
      }
      if (e.key === "Backspace" && !this.rowQueries[row]) {
        const externals = this.selectedExternals.filter((x) => x.role === row);
        if (externals.length > 0) {
          const last = externals[externals.length - 1];
          this.selectedExternals = this.selectedExternals.filter((x) => x !== last);
          return;
        }
        const users = this.selectedRecipients.filter((r) => r.role === row);
        if (users.length > 0) {
          const last = users[users.length - 1];
          this.selectedRecipients = this.selectedRecipients.filter((r) => r !== last);
        }
      }
    },

    // ── Beteiligten direkt anlegen (AddParticipantDialog) ────────────────

    openAddParticipant() {
      if (!this.selectedCase) return;
      this._involvedIdsBefore = new Set((this.caseInvolved || []).map((p) => p.id));
      this.$nextTick(() => this.$refs.addParticipantDialog?.openDialog());
    },

    /**
     * Nach erfolgreichem Anlegen: Beteiligtenliste refreshen und neu
     * hinzugekommene Beteiligte mit E-Mail automatisch als Empfänger der
     * aktiven Zeile übernehmen.
     */
    async onParticipantAdded() {
      const before = this._involvedIdsBefore || new Set();
      await this.loadCaseInvolved();
      const fresh = (this.caseInvolved || []).filter((p) => !before.has(p.id) && !p.removedAt);
      for (const p of fresh) {
        if (p.email) this.addExternalEmail(p.email, p.name || "", this.activeRow);
      }
      this._involvedIdsBefore = new Set((this.caseInvolved || []).map((p) => p.id));
      // Wenn das Auswahlfenster offen ist: Zeilen neu aufbauen, damit der
      // frisch angelegte Beteiligte sofort auswählbar ist.
      if (this.showRecipientPicker) this.openRecipientPicker();
    },

    onParticipantRemoveRequest() {
      useSnackbarStore().showSnackbar({
        message: "Beteiligte können im Bereich Beteiligte der Fallakte entfernt werden.",
        type: "warning",
      });
    },

    onAddMandantRequest() {
      useSnackbarStore().showSnackbar({
        message: "Kunden/Mandanten legen Sie bitte direkt in der Fallakte an.",
        type: "warning",
      });
    },

    /** Auswahl aus dem Dialog übernehmen: Dokumente als Referenz, E-Mails als PDF. */
    /** „Anhang vom PC“ aus der Werkzeugleiste. */
    addFiles(files) {
      for (const file of files || []) {
        if (file instanceof File) this.composeAttachments.push(file);
      }
    },

    onPickerAttach({ documents, files }) {
      for (const entry of documents || []) {
        this.pushDocAttachment(entry.doc, entry.idCase, entry.caseNumber);
      }
      for (const file of files || []) {
        if (file instanceof File) this.composeAttachments.push(file);
      }
    },

    /** Dokumentliste einer Akte (gecached je Fall-ID, best-effort). */
    async loadDocumentsForCase(idCase) {
      const key = String(idCase);
      if (this.docListCache[key]) return this.docListCache[key];
      try {
        const res = await fetch(`${API_BASE_URL}/protected/cases/${idCase}/documents`, { credentials: "include" });
        if (!res.ok) return [];
        const list = await res.json();
        const docs = Array.isArray(list) ? list : [];
        this.docListCache = { ...this.docListCache, [key]: docs };
        return docs;
      } catch { return []; }
    },

    /**
     * Anhänge aus Dokumentreferenzen ({ idCase, idDocument }) übernehmen –
     * z. B. „Per E-Mail senden“ aus der Dokumentenliste der Fallakte.
     */
    async addAttachmentRefs(refs) {
      const list = Array.isArray(refs) ? refs : [];
      const byCase = new Map();
      for (const r of list) {
        const idCase = Number(r?.idCase);
        const idDocument = Number(r?.idDocument);
        if (!idCase || !idDocument) continue;
        if (!byCase.has(idCase)) byCase.set(idCase, []);
        byCase.get(idCase).push(idDocument);
      }
      for (const [idCase, ids] of byCase) {
        const docs = await this.loadDocumentsForCase(idCase);
        const caseInfo = this.cases.find((c) => Number(c.id) === idCase);
        for (const idDocument of ids) {
          const doc = docs.find((d) => Number(d.id) === idDocument);
          if (!doc) continue;
          this.pushDocAttachment(doc, idCase, caseInfo?.caseNumber || "");
        }
      }
    },

    pushDocAttachment(doc, idCase, caseNumber) {
      // Kundenfeedback 30.09.2026 (I): gesperrte Dokumente nie anhängen.
      if (doc.locked) {
        useSnackbarStore().showSnackbar({
          message: this.$gt("„{title}“ ist gesperrt und wird nicht angehängt.").replace("{title}", doc.title || doc.filename),
          type: "warning",
        });
        return;
      }
      if (this.composeAttachments.some((a) => a.docId === doc.id && Number(a.idCase) === Number(idCase))) return;
      this.composeAttachments.push({
        name: doc.filename || doc.title, size: doc.fileSize,
        docId: doc.id, idCase: Number(idCase), caseNumber: caseNumber || "",
        filename: doc.filename, fileSize: doc.fileSize,
      });
    },

    /** Basis-URL der Konversation: fallgebunden oder fallfrei. */
    conversationBase(convId) {
      return this.selectedCase
        ? `${API_BASE_URL}/protected/cases/${this.selectedCase.id}/conversations/${convId}`
        : `${API_BASE_URL}/protected/messages/conversations/${convId}`;
    },

    /**
     * Versand an externe Empfänger auslösen, nachdem die Anhänge hochgeladen
     * sind. Das Backend hat die Mail wegen deferExternalSend zurückgehalten;
     * ohne diesen Aufruf ginge sie gar nicht raus.
     */
    async dispatchExternals(convId, messageId) {
      try {
        const res = await fetch(
          `${this.conversationBase(convId)}/messages/${messageId}/dispatch-externals`,
          { method: "POST", credentials: "include" },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } catch {
        useSnackbarStore().showSnackbar({
          message: "Die E-Mail an die externen Empfänger konnte nicht ausgelöst werden. Bitte im Postausgang prüfen.",
          type: "error",
        });
      }
    },

    /**
     * Anhänge hochladen: Dateien vom PC per Multipart, Dokumente aus einer
     * Fallakte per Referenz (Backend kopiert die Datei und prüft den Zugriff
     * auf die Quellakte). Fehlgeschlagene Anhänge werden gemeldet.
     * Rückgabe: IDs der angelegten Anhänge (für die Ablage, IV.2).
     */
    async uploadAttachments(convId, messageId, files) {
      const base = this.conversationBase(convId);
      const failed = [];
      const attachmentIds = [];
      for (const file of files) {
        try {
          let res;
          if (file instanceof File) {
            const formData = new FormData();
            formData.append("file", file);
            res = await fetch(
              `${base}/messages/${messageId}/attachments`,
              { method: "POST", credentials: "include", body: formData },
            );
          } else if (file?.docId) {
            res = await fetch(
              `${base}/messages/${messageId}/attachments/from-document`,
              {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ idCase: Number(file.idCase), idDocument: Number(file.docId) }),
              },
            );
          } else {
            continue;
          }
          if (!res.ok) {
            failed.push(file.name || file.filename || "Anhang");
          } else {
            const created = await res.json().catch(() => null);
            const id = Number(created?.id ?? created?.data?.id);
            if (id) attachmentIds.push(id);
          }
        } catch {
          failed.push(file.name || file.filename || "Anhang");
        }
      }
      if (failed.length) {
        useSnackbarStore().showSnackbar({
          message: `Nicht angehängt: ${failed.join(", ")}`,
          type: "warning",
        });
      }
      return attachmentIds;
    },

    async send() {
      if (this.sending) return;
      // Getippte E-Mails in den Empfängerzeilen automatisch übernehmen –
      // ohne Enter-Zwang (Inhaber-Feedback I.10). Ungültige Eingaben führen
      // zu einer klaren Fehlermeldung.
      for (const row of ["to", "cc", "bcc"]) {
        const raw = (this.rowQueries[row] || "").trim();
        if (!raw) continue;
        if (!this.commitExternalFromQuery(row, { focusAfter: false })) {
          useSnackbarStore().showSnackbar({
            message: `„${raw}" ist keine gültige E-Mail-Adresse.`,
            type: "error",
          });
          return;
        }
      }
      if (!this.hasAnyRecipient) {
        useSnackbarStore().showSnackbar({
          message: "Die Nachricht kann nicht versandt werden, weil keine E-Mail-Adresse bzw. kein Empfänger eingetragen ist.",
          type: "error",
        });
        return;
      }
      if (!this.stripHtml(this.composeContent).trim()) {
        useSnackbarStore().showSnackbar({
          message: "Die Nachricht ist leer und kann nicht versandt werden.",
          type: "error",
        });
        return;
      }
      this.sending = true;
      try {
        // Teilnehmer inkl. Rolle (to/cc/bcc) gemäß Backend-Vertrag.
        const participants = this.selectedRecipients.map((r) => ({
          idUser: r.userId,
          role: r.role || "to",
        }));
        // Backend verlangt mindestens einen participantId; wenn nur externe
        // Empfänger gewählt sind, schicken wir die eigene UserId mit.
        const participantIds = this.selectedRecipients.length > 0
          ? this.selectedRecipients.map((r) => r.userId)
          : [useAuthStore().idUser];

        const externalsPayload = this.selectedExternals.map((ext) => ({
          email: ext.email,
          name: ext.name || "",
          role: ext.role || "to",
        }));

        // Mit Anhängen darf die Mail an Externe erst nach dem Upload raus
        // (deferExternalSend), sonst fehlt der Anhang beim Empfänger
        // (Kundenfeedback 25.09.2026, Akte 2416/26).
        const hasAttachments = this.composeAttachments.length > 0;
        // Ablage-Auswahl des Absenders (IV.2): nur mit Fall; dann übergeht
        // das Backend die automatische Ablage der Organisation.
        const filingChoice = this.selectedCase ? { ...this.sendFiling.choice.value } : null;
        const filingTargets = this.selectedCase ? this.sendFiling.folders.targets.value : null;
        const skipAutoFile = filingChoice ? this.sendFiling.plan.value.skipAutoFile : false;

        // Mit Fall: fallgebundene Konversation; ohne Fall: fallfreie
        // Konversation über den globalen Endpunkt (Inhaber-Feedback I.1).
        const endpoint = this.selectedCase
          ? `${API_BASE_URL}/protected/cases/${this.selectedCase.id}/conversations`
          : `${API_BASE_URL}/protected/messages/conversations`;
        const res = await fetch(
          endpoint,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              participantIds,
              participants,
              subject: this.composeSubject,
              message: this.composeContent,
              externals: externalsPayload,
              isUrgent: this.isUrgent,
              requestDeliveryReceipt: this.requestDeliveryReceipt,
              requestReadReceipt: this.requestReadReceipt,
              idVehicle: this.contextVehicleId ?? null,
              deferExternalSend: hasAttachments,
              skipAutoFile,
            }),
          }
        );
        if (!res.ok) {
          let msg = "Nachricht konnte nicht gesendet werden";
          try { const j = await res.json(); if (j?.message) msg = j.message; } catch { /* ignore */ }
          throw new Error(msg);
        }
        const conv = await res.json();

        let attachmentIds = [];
        if (hasAttachments && conv.lastMessage) {
          try {
            attachmentIds = await this.uploadAttachments(conv.id, conv.lastMessage.id, this.composeAttachments);
          } finally {
            // Auch bei fehlgeschlagenem Upload versenden — die Nachricht ist
            // bereits angelegt und darf nicht unbemerkt liegen bleiben.
            if (this.selectedExternals.length) {
              await this.dispatchExternals(conv.id, conv.lastMessage.id);
            }
          }
        }

        useSnackbarStore().showSnackbar({ message: "Nachricht gesendet", type: "success" });
        if (filingChoice && conv.lastMessage) {
          await this.fileAfterSend(conv.lastMessage.id, attachmentIds, filingChoice, filingTargets);
        }
        this.$emit("sent", { conversation: conv, idCase: this.selectedCase ? this.selectedCase.id : null });
        this.clearDraft();
        this.closeWindow();
      } catch (e) {
        useSnackbarStore().showSnackbar({ message: e.message, type: "error" });
      } finally {
        this.sending = false;
      }
    },

    /**
     * Gewählte Ablage nach dem Versand ausführen (IV.2). Ein Fehler wird
     * gemeldet, macht den Versand aber nicht rückgängig.
     */
    async fileAfterSend(messageId, attachmentIds, choice, targets) {
      if (!this.selectedCase) return;
      try {
        const message = await fileSentMessage({
          caseId: Number(this.selectedCase.id),
          messageId,
          attachmentIds,
          choice,
          targets,
        });
        if (message) useSnackbarStore().showSnackbar({ message, type: "success" });
      } catch (e) {
        useSnackbarStore().showSnackbar({
          message: `${this.$gt("Gesendet, aber nicht in der Akte abgelegt")}: ${e?.message || this.$gt("Ablage fehlgeschlagen")}`,
          type: "warning",
        });
      }
    },

    avatarColor(id) {
      return AVATAR_COLORS[Math.abs(id || 0) % AVATAR_COLORS.length];
    },

    truncate(s, n) {
      if (!s) return "";
      return s.length > n ? s.substring(0, n) + "…" : s;
    },

    stripHtml(html) {
      if (!html) return "";
      const div = document.createElement("div");
      div.innerHTML = html;
      return div.textContent || div.innerText || "";
    },

    formatFileSize(bytes) {
      if (!bytes) return "0 B";
      const units = ["B", "KB", "MB", "GB"];
      let i = 0;
      let size = bytes;
      while (size >= 1024 && i < units.length - 1) { size /= 1024; i++; }
      return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
    },

    attachOutsideClick() {
      this._handleDocClick = (ev) => {
        const wrap = this.$refs.caseWrap;
        if (wrap && !wrap.contains(ev.target)) {
          this.showCaseDropdown = false;
        }
        if (!ev.target.closest?.(".mail-recipient-add-wrap")) {
          this.recipientDropdown = false;
        }
        if (!ev.target.closest?.(".nm-chip")) {
          this.openChipMenu = null;
        }
      };
      document.addEventListener("mousedown", this._handleDocClick);
    },
    detachOutsideClick() {
      if (this._handleDocClick) {
        document.removeEventListener("mousedown", this._handleDocClick);
        this._handleDocClick = null;
      }
    },
  },

  beforeUnmount() {
    if (this.show) this.flushDraft();
    if (this.draftTickTimer) clearInterval(this.draftTickTimer);
    if (this.draftTimer) clearTimeout(this.draftTimer);
    window.removeEventListener("beforeunload", this.flushDraft);
    this.detachOutsideClick();
  },
};
</script>

<style scoped>
.mail-compose-card { display: flex; flex-direction: column; overflow: hidden; }

.mail-compose-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px;
  background: var(--grey-light-color);
  color: var(--dark-color);
  border-bottom: 1px solid var(--border-grey-color);
}
.mail-compose-title { font-size: 0.9rem; font-weight: 500; }
.mail-draft-indicator {
  font-size: 0.72rem;
  font-weight: 400;
  color: rgba(15, 23, 42, 0.55);
  margin-left: 6px;
  font-style: italic;
}

.mail-compose-fields { padding: 0; }

.nm-corr-row { padding-top: 6px; padding-bottom: 6px; }
.nm-corr-list { display: flex; flex-direction: column; gap: 6px; flex: 1; min-width: 0; }
.nm-corr-add,
.nm-corr-added {
  display: inline-flex; align-items: center; gap: 5px; align-self: flex-start;
  margin-top: 4px; font-size: 0.78rem; font-weight: 600;
}
.nm-corr-add {
  padding: 3px 8px; border: 1px solid #d9b44a; border-radius: 6px;
  background: #fff; color: #5c4100; cursor: pointer;
}
.nm-corr-add:hover { background: #fff3cc; }
.nm-corr-add:focus-visible { outline: 2px solid #b07d00; outline-offset: 2px; }
.nm-corr-added { color: #2e7d32; }
.mail-field-row {
  display: flex; align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid #eef0f3;
  position: relative;
}
.mail-field-label {
  width: 52px; font-size: 0.82rem; color: var(--grey-color);
  flex-shrink: 0; align-self: center;
}
.mail-field-input {
  flex: 1; border: none; outline: none;
  font-size: 0.88rem; color: var(--dark-color);
  padding: 4px 0; font-family: inherit; background: transparent;
}
.mail-field-input::placeholder { color: var(--border-grey-color); }
.mail-field-input:disabled { color: var(--grey-color); cursor: not-allowed; }

.mail-recipients-wrap {
  flex: 1; display: flex; align-items: center; gap: 6px;
  flex-wrap: wrap; min-height: 28px;
}
.mail-recipient-tag {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 8px 3px 3px;
  background: var(--grey-light-color);
  border-radius: 16px;
  font-size: 0.82rem; color: var(--dark-color);
}
.mail-recipient-initials {
  display: flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 50%;
  color: #fff; font-size: 0.6rem; font-weight: 600; text-transform: uppercase;
}
.mail-recipient-remove {
  display: flex; align-items: center;
  border: none; background: none; color: var(--grey-color);
  cursor: pointer; padding: 1px; border-radius: 50%;
}
.mail-recipient-remove:hover { background: rgba(0,0,0,0.08); }

.mail-recipient-add-wrap { position: relative; }
.mail-recipient-add-btn {
  display: flex; align-items: center; justify-content: center;
  width: 28px; height: 28px;
  border: 1px dashed var(--border-grey-color);
  border-radius: 50%;
  background: none; color: var(--grey-color);
  cursor: pointer; transition: all 0.1s;
}
.mail-recipient-add-btn:hover { border-color: var(--primary-color); color: var(--primary-color); }

.mail-recipient-dropdown {
  position: absolute; top: 100%; left: 0; margin-top: 4px;
  background: #fff; border: 1px solid var(--border-grey-color); border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  min-width: 220px; max-height: 220px; overflow-y: auto; z-index: 10;
}
.mail-recipient-option {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px; cursor: pointer;
  font-size: 0.85rem; color: var(--dark-color);
  transition: background 0.1s;
}
.mail-recipient-option:hover { background: var(--grey-light-color); }
.mail-avatar-initials--xs {
  width: 22px; height: 22px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  color: #fff; font-size: 0.6rem; font-weight: 600; text-transform: uppercase;
}

.nm-hint { font-size: 0.78rem; color: var(--grey-color); padding: 2px 4px; }

.nm-case-wrap { position: relative; flex: 1; display: flex; align-items: center; }
.nm-case-input-wrap { position: relative; display: flex; align-items: center; width: 100%; }
.nm-case-icon { position: absolute; left: 2px; color: var(--grey-color); pointer-events: none; }
.nm-case-input {
  flex: 1; border: none; outline: none; background: transparent;
  padding: 4px 0 4px 24px;
  font-size: 0.88rem; font-family: inherit; color: var(--dark-color);
}
.nm-case-input::placeholder { color: var(--border-grey-color); }
.nm-case-chip {
  display: inline-flex; align-items: center; gap: 6px;
  background: var(--primary-light-color, #eff6ff); color: var(--primary-color);
  border: 1px solid var(--primary-color); border-radius: 999px;
  padding: 2px 10px; font-size: 0.82rem; line-height: 1.4; cursor: pointer;
  max-width: 100%; height: 24px; overflow: hidden;
}
.nm-case-chip > svg { flex-shrink: 0; }
.nm-case-chip-num {
  font-weight: 600;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 220px;
}
.nm-case-chip-desc {
  color: var(--grey-color);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 260px;
}
.nm-case-chip-x { margin-left: 2px; }
.nm-case-dropdown {
  position: absolute; top: calc(100% + 4px); left: 0; right: 0;
  background: #fff; border: 1px solid var(--border-grey-color); border-radius: 10px;
  box-shadow: 0 10px 30px -12px rgba(15, 23, 42, .18);
  z-index: 20; max-height: 280px; overflow: auto;
}
.nm-case-opt {
  display: flex; align-items: center;
  padding: 9px 12px; gap: 10px; cursor: pointer;
}
.nm-case-opt:hover { background: var(--grey-light-color); }
.nm-case-opt-text { display: flex; flex-direction: column; min-width: 0; }
.nm-case-opt-num { font-weight: 600; font-size: 0.86rem; color: var(--dark-color); }
.nm-case-opt-desc {
  font-size: 0.76rem; color: var(--grey-color);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.nm-case-empty { padding: 14px; text-align: center; color: var(--grey-color); font-size: 0.85rem; }

.nm-dd-enter-active, .nm-dd-leave-active { transition: opacity .12s, transform .12s; }
.nm-dd-enter-from, .nm-dd-leave-to { opacity: 0; transform: translateY(-4px); }

/* Toolbar-/Editor-Stile leben jetzt im geteilten MessageComposer. */

.mail-pending-attachments {
  display: flex; flex-wrap: wrap; gap: 6px;
  padding: 8px 16px;
  border-top: 1px solid #eef0f3;
}
.mail-pending-file {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 10px;
  background: var(--primary-light-color);
  border-radius: 8px;
  font-size: 0.78rem; color: var(--primary-color);
}
.mail-pending-size { opacity: 0.6; font-size: 0.7rem; }
.mail-pending-remove {
  display: flex; align-items: center;
  border: none; background: none; color: var(--grey-color);
  cursor: pointer; padding: 1px; border-radius: 4px;
}
.mail-pending-remove:hover { background: rgba(0,0,0,0.06); }

.mail-compose-bottom {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 12px;
  border-top: 1px solid #eef0f3;
}
.mail-compose-bottom--dialog { padding: 10px 16px; }
/* Gemeinsame Werkzeugleiste (ComposeTools) füllt die Fußzeile. */
.nm-tools { flex: 1 1 auto; min-width: 0; }
.mail-action-btn {
  display: inline-flex; align-items: center; justify-content: center;
  gap: 6px;
  min-height: 32px; padding: 0 10px;
  border: 1px solid var(--border-grey-color);
  border-radius: 6px;
  background: #fff; color: var(--grey-color);
  font-size: 12px; line-height: 1; white-space: nowrap;
  cursor: pointer; transition: all 0.1s;
}
.mail-action-btn__label { font-size: 12px; }
.mail-action-btn:hover:not(:disabled):not(.mail-action-btn--disabled) {
  background: var(--grey-light-color); color: var(--dark-color);
}
.mail-action-btn:disabled,
.mail-action-btn--disabled { opacity: 0.4; cursor: not-allowed; }
.mail-action-btn--active { color: var(--primary-color); background: var(--primary-light-color); }
.mail-action-btn--discard:hover { color: var(--error-color); }

.d-none { display: none; }

.mail-pending-case {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--primary-color);
  background: var(--primary-light-color);
  border-radius: 999px;
  padding: 1px 7px;
}

/* Inline recipient input */
.mail-recipient-inline-input {
  flex: 1; min-width: 160px;
  border: none; outline: none; background: transparent;
  padding: 4px 2px;
  font-size: 0.86rem; color: var(--dark-color); font-family: inherit;
}
.mail-recipient-inline-input::placeholder { color: var(--border-grey-color); }
.mail-recipient-inline-input:disabled { cursor: not-allowed; color: var(--grey-color); }

/* Optionen rechts neben der Fallauswahl (Empfänger aus Akte, Eskalation,
   Übertragungs-/Lesebestätigung – Inhaber-Feedback I.3/I.7) */
.nm-topopts {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 12px; flex-wrap: wrap;
  margin-left: auto; padding-left: 16px;
}
.nm-topopt-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 10px;
  border: 1px solid var(--border-grey-color);
  border-radius: 999px;
  background: #fff;
  color: var(--grey-color); font-size: 0.76rem;
  cursor: pointer; transition: background 0.1s, color 0.1s;
}
.nm-topopt-btn:hover { background: var(--grey-light-color); color: var(--dark-color); }
.nm-topopt-check {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.74rem; color: var(--grey-color);
  cursor: pointer; user-select: none;
}
.nm-topopt-check--on { color: var(--primary-color); }
.nm-topopt-input { accent-color: var(--primary-color); cursor: pointer; }
.nm-topopt-info { opacity: 0.55; flex-shrink: 0; }
.nm-label-info { vertical-align: middle; opacity: 0.6; margin-left: 2px; }

/* E-Mail-Vorlagen / Textbausteine: Leiste oberhalb des Editors */
.nm-template-bar {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 6px 16px;
  border-top: 1px solid #eef0f3;
  background: #fbfcfe;
}
.nm-template-hint {
  display: inline-flex; align-items: center; gap: 5px;
  margin-left: auto;
  font-size: 0.74rem; color: #047857;
}
.nm-template-hint--warn { color: #b45309; }

/* Betreff-Vorschläge aus den Falldaten (Inhaber-Feedback I.6) */
/* Betreff mit Bausteinen (Kundenfeedback 24.10.2026, XI): Eingabe und
   Bausteine bilden eine Zeile; gewählt = blau mit weißer Schrift, sonst weiß. */
.nm-subject-row { align-items: flex-start; }
.nm-subject-row .mail-field-label { padding-top: 5px; align-self: flex-start; }
.nm-subject { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px; }
.nm-subject-parts { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.nm-subject-parts__caption {
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 0.72rem; font-weight: 600; color: var(--grey-color); cursor: help;
}
.nm-suggest-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 3px 10px;
  border: 1px solid var(--border-grey-color);
  border-radius: 999px;
  background: #fff;
  font-size: 0.74rem; cursor: pointer; transition: background 0.1s, border-color 0.1s;
}
.nm-suggest-chip:hover { border-color: var(--primary-color); }
.nm-suggest-chip:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; }
.nm-suggest-chip-label { color: var(--grey-color); }
.nm-suggest-chip-value { color: var(--dark-color); font-weight: 500; }
.nm-suggest-chip--on { background: var(--primary-color); border-color: var(--primary-color); }
.nm-suggest-chip--on .nm-suggest-chip-label { color: rgba(255, 255, 255, 0.85); }
.nm-suggest-chip--on .nm-suggest-chip-value { color: #fff; }
.nm-suggest-chip--on:hover { background: #0066c7; }

/* „Cockpit durchsuchen“ neben den Betreff-Chips (Inhaber-Feedback I.6) */
.nm-cockpit-search {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 2px 8px;
  border: 1px solid var(--border-grey-color);
  border-radius: 999px;
  background: #fff;
  transition: border-color 0.12s, box-shadow 0.12s;
}
.nm-cockpit-search:focus-within,
.nm-cockpit-search--active {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px var(--primary-light-color);
}
.nm-cockpit-search__icon { color: var(--grey-color); flex-shrink: 0; }
.nm-cockpit-search__input {
  width: 240px;
  border: none; outline: none; background: transparent;
  font-size: 0.76rem; color: var(--dark-color);
}
.nm-cockpit-search__input::placeholder { color: var(--grey-color); }
.nm-cockpit-search__clear {
  display: inline-flex; align-items: center;
  border: none; background: transparent; padding: 0;
  color: var(--grey-color); cursor: pointer;
}
.nm-cockpit-search__clear:hover { color: var(--dark-color); }
.nm-cockpit-empty { align-self: center; font-size: 0.74rem; color: var(--grey-color); }

/* Empfängerauswahl aus der Akte (RA-Micro-artiges Auswahlfenster, I.3) */
.nm-picker-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
.nm-picker-th {
  text-align: center; padding: 8px 10px; width: 52px;
  border-bottom: 2px solid var(--border-grey-color);
  font-weight: 600; color: var(--dark-color); font-size: 0.8rem;
}
.nm-picker-th--name { text-align: left; width: auto; }
.nm-picker-td { padding: 8px 10px; border-bottom: 1px solid #eef0f3; }
.nm-picker-td--check { text-align: center; }
.nm-picker-tr:hover { background: var(--grey-light-color); }
.nm-picker-name { color: var(--dark-color); }
.nm-picker-name { font-weight: 600; }
.nm-picker-role { color: var(--grey-color); }
.nm-picker-email { display: block; color: var(--primary-color); font-size: 0.78rem; }
.nm-picker-check {
  display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px;
  border: 1.5px solid var(--border-grey-color); border-radius: 4px;
  background: #fff; color: #fff;
  cursor: pointer; transition: background 0.1s, border-color 0.1s;
}
.nm-picker-check--on { background: var(--primary-color); border-color: var(--primary-color); }
.nm-picker-empty { padding: 20px; text-align: center; color: var(--grey-color); }

/* Anhänge-Label vor den Anhang-Chips (Inhaber-Feedback I.11) */
.nm-attach-label {
  align-self: center;
  font-size: 0.78rem; font-weight: 600; color: var(--grey-color);
  margin-right: 4px;
}

/* Verwerfen + Senden rechts unten (Inhaber-Feedback I.8, Variante 1) */
.mail-compose-send { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }

/* ═══ Fenstermodus (useDialogWindow, resizable) ═══ */
.nm-window {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.nm-window .mail-compose-fields { flex-shrink: 0; }
.nm-window .nm-composer {
  flex: 1 1 auto;
  min-height: 140px;
}
.nm-window .nm-composer :deep(.mail-editor) { min-height: 140px; }
.nm-window .mail-pending-attachments,
.nm-window .mail-compose-bottom { flex-shrink: 0; }

.nm-head-actions { display: flex; align-items: center; gap: 2px; }
.nm-head-btn {
  display: flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  border: none; border-radius: 6px;
  background: none; color: var(--grey-color);
  cursor: pointer; transition: background 0.1s, color 0.1s;
}
.nm-head-btn:hover { background: rgba(0, 0, 0, 0.06); color: var(--dark-color); }
.nm-head-urgent {
  display: inline-flex; align-items: center; gap: 4px;
  margin-left: 10px; padding: 2px 8px;
  border-radius: 999px;
  background: #fdecea; color: var(--error-color, #c62828);
  font-size: 0.7rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.03em;
  vertical-align: middle;
}

/* Heller Kopfbereich im KARS-Design: Header und Empfängerfelder heben sich
   über die grauen Flächen und die Trennlinie vom Nachrichtenfeld ab. */
.mail-compose-fields { background: #fff; border-bottom: 1px solid var(--border-grey-color); }
.mail-compose-fields .mail-field-row:last-child { border-bottom: none; }

/* Resize-Handles an Kanten und Ecken */
.nm-resize { position: absolute; z-index: 30; touch-action: none; }
.nm-resize--n { top: 0; left: 14px; right: 14px; height: 6px; cursor: ns-resize; }
.nm-resize--s { bottom: 0; left: 14px; right: 14px; height: 6px; cursor: ns-resize; }
.nm-resize--e { right: 0; top: 14px; bottom: 14px; width: 6px; cursor: ew-resize; }
.nm-resize--w { left: 0; top: 14px; bottom: 14px; width: 6px; cursor: ew-resize; }
.nm-resize--ne { top: 0; right: 0; width: 14px; height: 14px; cursor: nesw-resize; }
.nm-resize--nw { top: 0; left: 0; width: 14px; height: 14px; cursor: nwse-resize; }
.nm-resize--se { bottom: 0; right: 0; width: 14px; height: 14px; cursor: nwse-resize; }
.nm-resize--sw { bottom: 0; left: 0; width: 14px; height: 14px; cursor: nesw-resize; }

/* Aktive Ziel-Zeile dezent markieren (box-shadow: kein Layout-Shift) */
.mail-recipients-wrap--active { box-shadow: inset 2px 0 0 var(--primary-color); }

/* Empfänger-Pillen mit Verschieben-Menü */
.nm-chip { position: relative; }
.nm-chip-label {
  max-width: 260px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.nm-chip-menu-btn {
  display: flex; align-items: center;
  border: none; background: none; color: var(--grey-color);
  cursor: pointer; padding: 1px; border-radius: 50%;
}
.nm-chip-menu-btn:hover { background: rgba(0, 0, 0, 0.08); }
.nm-chip-menu {
  position: absolute; top: calc(100% + 4px); left: 0; z-index: 40;
  background: #fff; border: 1px solid var(--border-grey-color); border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14);
  min-width: 180px; overflow: hidden;
}
.nm-chip-menu-item {
  display: block; width: 100%; text-align: left;
  border: none; background: none;
  padding: 8px 12px;
  font-size: 0.8rem; color: var(--dark-color);
  cursor: pointer; transition: background 0.1s;
}
.nm-chip-menu-item:hover { background: var(--grey-light-color); }

</style>
